import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Search, Loader2, Sparkles, Phone, Mail, MapPin, Car, Users, Building2,
    Link2, User, CheckCircle2, AlertTriangle, Clock, RefreshCw, ChevronRight,
    Code2, Wallet, Fingerprint, History, Gauge, Copy, Check, ShieldCheck,
    Database, TrendingDown, Ban, ArrowUpRight, ListChecks, CalendarRange, X,
    FileDown, StopCircle, Network, CornerDownRight,
} from 'lucide-react';
import { Profile, LemitPessoaData, LemitEmpresaData, LemitTipo, LemitTelefone, LemitConsulta, LemitSocio } from '../../types';
import { supabase } from '../../src/lib/supabase';
import { hasLemitAccess } from '../../src/lib/lemitAcesso';
import { useAuth } from '../../src/contexts/AuthContext';
import { useToast } from '../../src/contexts/ToastContext';
import {
    consultarLemit, getLemitSaldo, getLemitLog, getLemitUsageStats,
    getLemitConsultaRaw, getLemitHistory, LemitLogRow, LemitUsageStats, LemitCriterio,
    listarRepresentantesPendentes, RepresentantePendente,
    getConsultasSalvasPorDoc, enriquecerSocio, SocioEnriquecido,
} from '../../src/services/lemit';
import {
    explorarCadeiaSocietaria, ordenarCadeia, pessoasDaCadeia, resumoCadeia, textoMotivoParada,
    NoCadeia, PreviaNivel, ResultadoCadeia,
} from '../../src/services/lemitCadeia';
import { exportarDossieLemitPdf } from '../../src/lib/lemitDossiePdf';
import { getGeocodeLog, getGeocodeStats, GeocodeLogRow, GeocodeStats } from '../../src/services/geocodeLog';
import { getGastoPorUsuario, GastoUsuario } from '../../src/services/custosApi';
import TablePagination from '../../src/components/TablePagination';

// ─────────────────────────────────────────────────────────────────────────────
// Central Lemit (Gestão) — consulta avulsa de CPF/CNPJ, central de custos e
// histórico completo, fora do contexto de uma negociação do CRM.
// ─────────────────────────────────────────────────────────────────────────────

// Quem acessa: src/lib/lemitAcesso.ts (reexportado para quem já importava daqui).
export { hasLemitAccess, LEMIT_ALLOWED_DEPARTAMENTOS } from '../../src/lib/lemitAcesso';

// ── Formatadores ──────────────────────────────────────────────────────────────
const formatDocumento = (doc?: string): string => {
    const c = (doc || '').replace(/\D/g, '');
    if (c.length === 11) return c.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    if (c.length === 14) return c.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    return doc || '—';
};
const maskDocInput = (digits: string): string => {
    if (digits.length > 11) {
        return digits
            .replace(/^(\d{2})(\d)/, '$1.$2')
            .replace(/^(\d{2})\.(\d{3})(\d)/, '$1.$2.$3')
            .replace(/^(\d{2})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3/$4')
            .replace(/^(\d{2})\.(\d{3})\.(\d{3})\/(\d{4})(\d)/, '$1.$2.$3/$4-$5');
    }
    return digits
        .replace(/^(\d{3})(\d)/, '$1.$2')
        .replace(/^(\d{3})\.(\d{3})(\d)/, '$1.$2.$3')
        .replace(/^(\d{3})\.(\d{3})\.(\d{3})(\d)/, '$1.$2.$3-$4');
};
const formatPhone = (p: LemitTelefone): string => {
    const n = (p.numero || '').replace(/\D/g, '');
    const num = n.length >= 9 ? `${n.slice(0, n.length - 4)}-${n.slice(-4)}` : n;
    return `(${p.ddd ?? '--'}) ${num}`;
};
const fmtBRL = (v?: number | null) => v != null ? v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : '—';
/** Geocode do Google é cobrado em dólar (a Lemit é em real). */
const fmtUsd = (v?: number | null) => v != null ? `US$ ${v.toFixed(2).replace('.', ',')}` : '—';
const fmtDate = (iso?: string) => {
    if (!iso) return '—';
    const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};
const fmtDateTime = (iso?: string) => iso ? new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
const timeAgo = (dateStr?: string): string => {
    if (!dateStr) return '';
    const diffMin = Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000);
    if (diffMin < 1) return 'agora mesmo';
    if (diffMin < 60) return `há ${diffMin} min`;
    const h = Math.floor(diffMin / 60);
    if (h < 24) return `há ${h}h`;
    return `há ${Math.floor(h / 24)} dia(s)`;
};
const initials = (nome?: string): string => {
    const parts = (nome || '?').trim().split(/\s+/);
    return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase() || '?';
};

type Period = 'mes' | '30d' | 'tudo' | 'custom';
const periodStart = (p: Period): string | undefined => {
    const now = new Date();
    if (p === 'mes') return new Date(now.getFullYear(), now.getMonth(), 1).toISOString();
    if (p === '30d') return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    return undefined;
};
const PERIOD_LABEL: Record<Period, string> = { mes: 'este mês', '30d': 'últimos 30 dias', tudo: 'todo o período', custom: 'período selecionado' };

type ResultState = {
    data: LemitPessoaData | LemitEmpresaData;
    raw: any;
    tipo: LemitTipo;
    source: 'api' | 'cache';
    cached_at: string;
    cached_by?: string | null;
} | null;

// ── Blocos visuais ────────────────────────────────────────────────────────────
const card = 'bg-white dark:bg-slate-900/60 border border-slate-200/80 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden';
const sectionHead = 'flex items-center gap-2.5 px-5 py-3.5 border-b border-slate-100 dark:border-slate-800';

/** Textura de ruído sutil pro hero (SVG inline). */
const NOISE = "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.8' numOctaves='3'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.35'/%3E%3C/svg%3E\")";

const SectionCard: React.FC<{ icon: React.ReactNode; title: string; count?: number; tint?: string; action?: React.ReactNode; children: React.ReactNode; delay?: number }> =
    ({ icon, title, count, tint = 'text-primary bg-primary/8 dark:bg-primary/15', action, children, delay = 0 }) => (
        <div className={`${card} animate-slide-up opacity-0`} style={{ animationDelay: `${delay}ms` }}>
            <div className={sectionHead}>
                <div className={`p-1.5 rounded-lg ${tint}`}>{icon}</div>
                <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">{title}</h4>
                {count != null && count > 0 && (
                    <span className="px-2 py-0.5 text-[10px] font-bold bg-primary/10 text-primary rounded-full">{count}</span>
                )}
                {action && <div className="ml-auto">{action}</div>}
            </div>
            <div className="p-4">{children}</div>
        </div>
    );

const InfoCell: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
    <div className="space-y-0.5">
        <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider">{label}</p>
        <p className="text-sm font-semibold text-slate-700 dark:text-slate-200 break-words">{value || '—'}</p>
    </div>
);

const EmptyRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <p className="text-xs text-slate-400 italic py-2 text-center">{children}</p>
);

// ── View ──────────────────────────────────────────────────────────────────────
const LemitView: React.FC = () => {
    const { profile } = useAuth();
    const { addToast } = useToast();

    const [tab, setTab] = useState<'dossie' | 'historico' | 'pendentes' | 'geocode'>('dossie');

    // Custo do geocode do Google (Lista Fria) — API paga, separada da Lemit
    const [geoLog, setGeoLog] = useState<GeocodeLogRow[]>([]);
    const [geoStats, setGeoStats] = useState<GeocodeStats | null>(null);
    const [geoLoading, setGeoLoading] = useState(false);
    // Custos consolidados por usuário (quem/quanto)
    const [gastos, setGastos] = useState<GastoUsuario[]>([]);
    // Paginação das tabelas (histórico e sócios pendentes)
    const [logPage, setLogPage] = useState(0);
    const [logPageSize, setLogPageSize] = useState(50);
    const [pendPage, setPendPage] = useState(0);
    const [pendPageSize, setPendPageSize] = useState(50);

    // Sócios pendentes: leads com representante definido mas CPF nunca consultado
    const [pendentes, setPendentes] = useState<RepresentantePendente[]>([]);
    const [pendLoading, setPendLoading] = useState(false);
    const [pendOp, setPendOp] = useState<{ done: number; total: number; ok: number } | null>(null);
    const pendCancelRef = useRef(false);
    useEffect(() => { setPendPage(0); }, [pendentes]);
    const pendPagina = useMemo(() => pendentes.slice(pendPage * pendPageSize, pendPage * pendPageSize + pendPageSize), [pendentes, pendPage, pendPageSize]);

    // Busca
    const [docDigits, setDocDigits] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<ResultState>(null);
    const [error, setError] = useState<string | null>(null);
    const [confirming, setConfirming] = useState(false);
    const [forceMode, setForceMode] = useState(false);
    const [docHistory, setDocHistory] = useState<LemitConsulta[]>([]);
    const inputRef = useRef<HTMLInputElement>(null);
    const topRef = useRef<HTMLDivElement>(null);

    // Custos configurados
    const [custoPessoa, setCustoPessoa] = useState<number | null>(null);
    const [custoEmpresa, setCustoEmpresa] = useState<number | null>(null);

    // Central de custos
    const [saldo, setSaldo] = useState<any>(null);
    const [saldoLoading, setSaldoLoading] = useState(false);
    const [saldoError, setSaldoError] = useState<string | null>(null);
    const [period, setPeriod] = useState<Period>('mes');
    // Filtro de datas custom (De/Até) — sobrepõe os presets quando period === 'custom'.
    const [customFrom, setCustomFrom] = useState('');  // YYYY-MM-DD
    const [customTo, setCustomTo] = useState('');
    const activeRange = useMemo<{ from?: string; to?: string }>(() => {
        if (period === 'custom') return {
            from: customFrom ? new Date(customFrom + 'T00:00:00').toISOString() : undefined,
            to: customTo ? new Date(customTo + 'T23:59:59.999').toISOString() : undefined,
        };
        return { from: periodStart(period) };
    }, [period, customFrom, customTo]);
    const [stats, setStats] = useState<LemitUsageStats | null>(null);
    const [log, setLog] = useState<LemitLogRow[]>([]);
    const [logLoading, setLogLoading] = useState(false);

    // Histórico
    const [histSearch, setHistSearch] = useState('');
    const [expanded, setExpanded] = useState<number | null>(null);
    const [rawCache, setRawCache] = useState<Record<number, any>>({});
    const [openingId, setOpeningId] = useState<number | null>(null);

    // Quadro societário — dossiê de todos os sócios de uma vez
    const [sociosEnr, setSociosEnr] = useState<Record<string, SocioEnriquecido>>({});
    const [sociosPrep, setSociosPrep] = useState(false);
    const [sociosConfirm, setSociosConfirm] = useState<{ alvos: LemitSocio[]; salvos: Map<string, LemitConsulta>; gerarPdf: boolean } | null>(null);
    const [sociosOp, setSociosOp] = useState<{ done: number; total: number; ok: number } | null>(null);
    const sociosCancelRef = useRef(false);

    // Cadeia societária — expansão recursiva (sócio que é CNPJ abre o próprio quadro)
    const [cadeia, setCadeia] = useState<NoCadeia[] | null>(null);
    const [cadeiaRes, setCadeiaRes] = useState<ResultadoCadeia | null>(null);
    const [cadeiaSetup, setCadeiaSetup] = useState(false);
    const [cadeiaCfg, setCadeiaCfg] = useState({ maxNivel: 3, maxConsultas: 40, gerarPdf: true });
    const [cadeiaOp, setCadeiaOp] = useState<{ nivel: number; done: number; total: number } | null>(null);
    const [cadeiaConfirm, setCadeiaConfirm] = useState<PreviaNivel | null>(null);
    const cadeiaResolverRef = useRef<((ok: boolean) => void) | null>(null);
    const cadeiaCancelRef = useRef(false);

    // Micro-feedback de cópia
    const [copiedKey, setCopiedKey] = useState<string | null>(null);
    const copy = async (key: string, value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            setCopiedKey(key);
            setTimeout(() => setCopiedKey((k) => (k === key ? null : k)), 1500);
        } catch {
            addToast('Não foi possível copiar.', 'error');
        }
    };

    const apiCriterio: LemitCriterio | null = useMemo(() => {
        if (docDigits.length === 11) return 'cpf';
        if (docDigits.length === 14) return 'cnpj';
        return null;
    }, [docDigits]);
    const valorValido = apiCriterio != null;
    const targetLen = docDigits.length > 11 ? 14 : 11;
    const custoConsulta = apiCriterio === 'cnpj' ? custoEmpresa : custoPessoa;

    const loadSaldo = useCallback(async () => {
        setSaldoLoading(true); setSaldoError(null);
        try {
            setSaldo(await getLemitSaldo());
        } catch (e: any) {
            setSaldoError(e.message || 'Não foi possível obter o saldo.');
        } finally {
            setSaldoLoading(false);
        }
    }, []);

    const loadUsage = useCallback(async (range: { from?: string; to?: string }) => {
        setLogLoading(true);
        try {
            const [s, rows] = await Promise.all([
                getLemitUsageStats(range.from, undefined, range.to),
                getLemitLog(range),
            ]);
            setStats(s);
            setLog(rows);
        } finally {
            setLogLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!hasLemitAccess(profile)) return;
        loadSaldo();
        loadUsage(activeRange);
        (async () => {
            const { data: cfgs } = await supabase.from('system_settings').select('key, value').in('key', ['lemit_custo_pessoa', 'lemit_custo_empresa']);
            (cfgs || []).forEach((c: any) => {
                const p = parseFloat(String(c.value).replace(',', '.'));
                if (!isNaN(p)) { if (c.key === 'lemit_custo_pessoa') setCustoPessoa(p); else setCustoEmpresa(p); }
            });
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (hasLemitAccess(profile)) loadUsage(activeRange);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period, customFrom, customTo]);

    // Economia por cache no período (consultas que não geraram custo)
    const cacheStats = useMemo(() => {
        const cacheRows = log.filter((r) => r.source === 'cache');
        let economia = 0;
        let conhecida = false;
        cacheRows.forEach((r) => {
            const c = r.tipo === 'empresa' ? custoEmpresa : custoPessoa;
            if (c != null) { economia += c; conhecida = true; }
        });
        return { count: cacheRows.length, economia, conhecida };
    }, [log, custoPessoa, custoEmpresa]);

    // ── Sócios pendentes (enriquecimento em lote dos CPFs dos representantes) ──────
    const carregarPendentes = async () => {
        setPendLoading(true);
        try {
            setPendentes(await listarRepresentantesPendentes());
        } catch (e: any) {
            addToast('Erro ao carregar pendências: ' + (e.message || ''), 'error');
        } finally {
            setPendLoading(false);
        }
    };

    useEffect(() => { if (tab === 'pendentes' && pendentes.length === 0 && !pendLoading) carregarPendentes(); }, [tab]);

    const carregarGeocode = async () => {
        setGeoLoading(true);
        try {
            const ini = new Date(); ini.setDate(1); ini.setHours(0, 0, 0, 0);
            const [log, stats, g] = await Promise.all([
                getGeocodeLog(), getGeocodeStats(ini.toISOString()), getGastoPorUsuario(ini.toISOString()),
            ]);
            setGeoLog(log); setGeoStats(stats); setGastos(g);
        } finally { setGeoLoading(false); }
    };

    useEffect(() => { if (tab === 'geocode' && !geoStats && !geoLoading) carregarGeocode(); }, [tab]);

    /** Consulta o CPF de cada representante pendente, em série e com throttle (limite Lemit 10/s). */
    const enriquecerPendentes = async (alvos: RepresentantePendente[]) => {
        const custo = custoPessoa != null ? `\n\nCusto estimado: até ${fmtBRL(custoPessoa * alvos.length)} (${alvos.length} × ${fmtBRL(custoPessoa)}).` : '';
        if (!window.confirm(`Consultar o CPF de ${alvos.length} representante(s) na Lemit?${custo}`)) return;

        pendCancelRef.current = false;
        setPendOp({ done: 0, total: alvos.length, ok: 0 });
        let ok = 0;
        for (let i = 0; i < alvos.length; i++) {
            if (pendCancelRef.current) break;
            try {
                // sem `segmento`: em produção ele é repassado à Lemit e causa 404
                await consultarLemit({
                    criterio: 'cpf',
                    valor: alvos[i].cpf_representante,
                    negociacaoId: alvos[i].negociacao_id,
                });
                ok++;
            } catch (e) {
                console.error('pendente', alvos[i].cpf_representante, e);
            }
            setPendOp({ done: i + 1, total: alvos.length, ok });
            await new Promise((r) => setTimeout(r, 180));
        }
        setPendOp(null);
        addToast(`${ok}/${alvos.length} representante(s) enriquecido(s).`, ok > 0 ? 'success' : 'error');
        await carregarPendentes();
        loadUsage(activeRange);
    };

    // ── Quadro societário: dossiê de todos os sócios num clique ───────────────────
    /** Sócios do dossiê aberto que têm documento consultável. */
    const sociosDaEmpresa = useMemo<LemitSocio[]>(() => {
        if (!result || result.tipo !== 'empresa') return [];
        return ((result.data as LemitEmpresaData).socios || []).filter((s) => {
            const d = (s.cpf || '').replace(/\D/g, '');
            return d.length === 11 || d.length === 14;
        });
    }, [result]);

    const sociosFaltando = useMemo(
        () => sociosDaEmpresa.filter((s) => !sociosEnr[(s.cpf || '').replace(/\D/g, '')]),
        [sociosDaEmpresa, sociosEnr],
    );

    /**
     * Ao abrir um dossiê de empresa, traz de graça o que já está salvo dos sócios
     * (uma consulta ao banco, zero custo) — assim o botão de enriquecer só paga
     * pelos que realmente faltam.
     */
    useEffect(() => {
        setSociosEnr({});
        setCadeia(null);
        setCadeiaRes(null);
        setCadeiaSetup(false);
        if (sociosDaEmpresa.length === 0) return;
        let cancelado = false;
        (async () => {
            try {
                const salvos = await getConsultasSalvasPorDoc(sociosDaEmpresa.map((s) => s.cpf || ''));
                if (cancelado || salvos.size === 0) return;
                const acc: Record<string, SocioEnriquecido> = {};
                for (const s of sociosDaEmpresa) {
                    const d = (s.cpf || '').replace(/\D/g, '');
                    const salvo = salvos.get(d);
                    if (salvo) acc[d] = await enriquecerSocio(s, { salvo });  // com `salvo` não bate na API
                }
                if (!cancelado) setSociosEnr(acc);
            } catch (e) {
                console.error('pré-carga de sócios salvos', e);
            }
        })();
        return () => { cancelado = true; };
    }, [sociosDaEmpresa]);

    /** Sócios na ordem do quadro societário (a ordem das páginas do PDF). */
    const sociosParaPdf = (mapa: Record<string, SocioEnriquecido>): SocioEnriquecido[] =>
        sociosDaEmpresa.map((s) => mapa[(s.cpf || '').replace(/\D/g, '')]).filter(Boolean);

    const gerarPdfDossie = (
        mapa?: Record<string, SocioEnriquecido>,
        cadeiaPdf?: { nos: NoCadeia[]; nota?: string | null } | null,
    ) => {
        if (!result) return;
        const arvore = cadeiaPdf ?? (cadeia ? { nos: cadeia, nota: cadeiaRes ? textoMotivoParada(cadeiaRes) : null } : null);
        try {
            exportarDossieLemitPdf({
                tipo: result.tipo,
                data: result.data,
                socios: sociosParaPdf(mapa ?? sociosEnr),
                cadeia: arvore?.nos,
                notaCadeia: arvore?.nota,
                geradoPor: profile?.full_name,
                consultadoEm: result.cached_at,
            });
            addToast('PDF do dossiê gerado.', 'success');
        } catch (e: any) {
            addToast('Falha ao gerar o PDF: ' + (e.message || ''), 'error');
        }
    };

    /** Abre a confirmação de custo já sabendo quantos sócios saem de graça. */
    const abrirConfirmSocios = async (gerarPdf: boolean) => {
        if (sociosDaEmpresa.length === 0) return;
        // nada a consultar: não faz sentido pedir confirmação de custo zero
        if (sociosFaltando.length === 0) { if (gerarPdf) gerarPdfDossie(); return; }
        setSociosPrep(true);
        try {
            const salvos = await getConsultasSalvasPorDoc(sociosDaEmpresa.map((s) => s.cpf || ''));
            setSociosConfirm({ alvos: sociosDaEmpresa, salvos, gerarPdf });
        } catch (e: any) {
            addToast('Não foi possível checar o que já está salvo: ' + (e.message || ''), 'error');
        } finally {
            setSociosPrep(false);
        }
    };

    /** Prévia do lote: quantos vêm do que já foi pago e quanto custam os novos. */
    const previaSocios = useMemo(() => {
        if (!sociosConfirm) return null;
        let cacheados = 0, novosPessoa = 0, novosEmpresa = 0;
        sociosConfirm.alvos.forEach((s) => {
            const d = (s.cpf || '').replace(/\D/g, '');
            if (sociosConfirm.salvos.has(d)) { cacheados++; return; }
            if (d.length === 14) novosEmpresa++; else novosPessoa++;
        });
        const custoConhecido = (novosPessoa === 0 || custoPessoa != null) && (novosEmpresa === 0 || custoEmpresa != null);
        return {
            cacheados,
            novos: novosPessoa + novosEmpresa,
            custo: custoConhecido ? novosPessoa * (custoPessoa ?? 0) + novosEmpresa * (custoEmpresa ?? 0) : null,
        };
    }, [sociosConfirm, custoPessoa, custoEmpresa]);

    /** Roda o lote em série com throttle (a Lemit limita ~10 req/s). */
    const rodarEnriquecimentoSocios = async () => {
        if (!sociosConfirm) return;
        const { alvos, salvos, gerarPdf } = sociosConfirm;
        setSociosConfirm(null);
        sociosCancelRef.current = false;
        setSociosOp({ done: 0, total: alvos.length, ok: 0 });

        const acc: Record<string, SocioEnriquecido> = { ...sociosEnr };
        let ok = 0;
        let limiteAtingido = false;
        for (let i = 0; i < alvos.length; i++) {
            if (sociosCancelRef.current) break;
            const d = (alvos[i].cpf || '').replace(/\D/g, '');
            const salvo = salvos.get(d) ?? null;
            try {
                const enr = await enriquecerSocio(alvos[i], { salvo });
                acc[d] = enr;
                if (enr.status === 'ok') ok++;
            } catch (e: any) {
                // só erro de LIMITE da conta chega aqui — as próximas também falhariam
                limiteAtingido = true;
                addToast(e.message || 'Limite da conta Lemit atingido — lote interrompido.', 'error');
                break;
            }
            setSociosOp({ done: i + 1, total: alvos.length, ok });
            if (!salvo) await new Promise((r) => setTimeout(r, 180));
        }
        setSociosEnr(acc);
        setSociosOp(null);
        if (sociosCancelRef.current) addToast(`Lote interrompido — ${ok} sócio(s) enriquecido(s) até aqui.`, 'warning');
        else if (!limiteAtingido) addToast(`${ok}/${alvos.length} sócio(s) com dossiê completo.`, ok > 0 ? 'success' : 'warning');
        loadUsage(activeRange);
        if (gerarPdf && !sociosCancelRef.current) gerarPdfDossie(acc);
    };

    // ── Cadeia societária: desce nos sócios que são CNPJ até chegar nos CPFs ──────
    /**
     * Confirmação de custo de CADA nível. Um nível que só reaproveita consultas já
     * pagas passa direto — só interrompe quando vai gastar de verdade.
     */
    const confirmarNivelCadeia = (previa: PreviaNivel) => new Promise<boolean>((resolve) => {
        if (previa.novosPessoa + previa.novosEmpresa === 0) { resolve(true); return; }
        cadeiaResolverRef.current = resolve;
        setCadeiaConfirm(previa);
    });

    const responderNivelCadeia = (ok: boolean) => {
        setCadeiaConfirm(null);
        const resolver = cadeiaResolverRef.current;
        cadeiaResolverRef.current = null;
        resolver?.(ok);
    };

    const rodarCadeia = async () => {
        if (!result || result.tipo !== 'empresa') return;
        const empresaRaiz = result.data as LemitEmpresaData;
        // o documento da RAIZ vem do dossiê, não da caixa de busca (que o botão
        // "investigar" reescreve) — é ele que amarra a árvore no PDF
        const raizDoc = (empresaRaiz.cnpj || '').replace(/\D/g, '') || docDigits;
        setCadeiaSetup(false);
        cadeiaCancelRef.current = false;
        setCadeia(null);
        setCadeiaRes(null);
        setCadeiaOp({ nivel: 1, done: 0, total: sociosDaEmpresa.length });
        try {
            const res = await explorarCadeiaSocietaria({
                raizDoc,
                raizNome: empresaRaiz.razao_social || empresaRaiz.nome_fantasia || 'Empresa',
                raizData: empresaRaiz,
                maxNivel: cadeiaCfg.maxNivel,
                maxConsultas: cadeiaCfg.maxConsultas,
                custoPessoa, custoEmpresa,
                jaEnriquecidos: sociosEnr,
                confirmarNivel: confirmarNivelCadeia,
                onProgresso: ({ nivel, done, total }) => setCadeiaOp({ nivel, done, total }),
                cancelado: () => cadeiaCancelRef.current,
            });
            const ordenada = ordenarCadeia(res.nos, raizDoc);
            setCadeia(ordenada);
            setCadeiaRes(res);

            // Sócios diretos alimentam de volta a tabela do quadro societário
            const diretos: Record<string, SocioEnriquecido> = { ...sociosEnr };
            res.nos.filter((n) => n.nivel === 1 && n.data).forEach((n) => { diretos[n.cpf] = n; });
            setSociosEnr(diretos);

            const r = resumoCadeia(ordenada);
            addToast(
                `Cadeia: ${r.empresas} empresa(s) e ${r.pessoas} CPF(s) em ${r.nivelMax} nível(is). ${textoMotivoParada(res)}`,
                res.motivoParada === 'limite_conta' ? 'error' : r.pessoas > 0 ? 'success' : 'warning',
            );
            loadUsage(activeRange);
            if (cadeiaCfg.gerarPdf && ordenada.length > 0) {
                gerarPdfDossie(diretos, { nos: ordenada, nota: textoMotivoParada(res) });
            }
        } catch (e: any) {
            addToast('Falha ao expandir a cadeia: ' + (e.message || ''), 'error');
        } finally {
            setCadeiaOp(null);
            responderNivelCadeia(false);  // solta um confirm que tenha ficado pendurado
        }
    };

    /** Pessoas físicas no fim da cadeia (o alvo da expansão). */
    const pessoasCadeia = useMemo(() => (cadeia ? pessoasDaCadeia(cadeia) : []), [cadeia]);

    const openConfirm = async (force: boolean) => {
        if (!valorValido) { addToast('Digite um CPF (11 dígitos) ou CNPJ (14 dígitos) válido.', 'warning'); return; }
        setForceMode(force);
        setConfirming(true);
        setDocHistory(await getLemitHistory(docDigits));
    };

    const runConsulta = async (force: boolean) => {
        if (!apiCriterio) return;
        setConfirming(false);
        setLoading(true);
        setError(null);
        setTab('dossie');
        try {
            // Não passar `segmento`: a edge function em produção repassa o campo à
            // API da Lemit, que responde 404 para valores não cadastrados na conta.
            const r = await consultarLemit({ criterio: apiCriterio, valor: docDigits, forceRefresh: force });
            setResult({ data: r.data, raw: r.raw, tipo: r.tipo, source: r.source, cached_at: r.cached_at, cached_by: r.cached_by });
            addToast(r.source === 'cache' ? 'Dados recuperados do cache (sem custo).' : 'Consulta Lemit realizada com sucesso!', 'success');
            loadUsage(activeRange);
        } catch (e: any) {
            const msg = e.message || 'Erro na consulta Lemit';
            setError(msg);
            const notFound = /nao encontrado|não encontrado|\[404\]/i.test(msg);
            addToast(notFound ? 'Documento não encontrado na base da Lemit.' : msg, notFound ? 'warning' : 'error');
        } finally {
            setLoading(false);
        }
    };

    /** Reabre uma consulta já registrada (sem custo) direto do histórico. */
    const openDossieFromLog = async (row: LemitLogRow) => {
        setOpeningId(row.id);
        try {
            const { data } = await supabase.from('lemit_consultas').select('*').eq('id', row.id).single();
            if (!data) { addToast('Consulta não encontrada.', 'error'); return; }
            const c = data as LemitConsulta;
            setResult({
                data: c.resultado, raw: c.resultado_raw, tipo: c.tipo,
                source: 'cache', cached_at: c.created_at, cached_by: c.consultado_por_nome,
            });
            setDocDigits((c.documento || '').replace(/\D/g, ''));
            setError(null);
            setTab('dossie');
            topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } finally {
            setOpeningId(null);
        }
    };

    const toggleRaw = async (id: number) => {
        if (expanded === id) { setExpanded(null); return; }
        setExpanded(id);
        if (!rawCache[id]) {
            const raw = await getLemitConsultaRaw(id);
            setRawCache((c) => ({ ...c, [id]: raw }));
        }
    };

    const filteredLog = useMemo(() => {
        const q = histSearch.trim().toLowerCase();
        if (!q) return log;
        const qDigits = q.replace(/\D/g, '');
        return log.filter((r) =>
            (qDigits.length >= 3 && (r.documento || '').includes(qDigits)) ||
            (r.consultado_por_nome || '').toLowerCase().includes(q) ||
            (r.negociacoes?.titulo || '').toLowerCase().includes(q) ||
            (r.negociacoes?.cliente_nome || '').toLowerCase().includes(q),
        );
    }, [log, histSearch]);

    // Página do histórico (reseta quando a lista filtrada muda — busca/recarga)
    useEffect(() => { setLogPage(0); }, [histSearch, log, period]);
    const logPagina = useMemo(
        () => filteredLog.slice(logPage * logPageSize, logPage * logPageSize + logPageSize),
        [filteredLog, logPage, logPageSize],
    );

    // ── Sem acesso ────────────────────────────────────────────────────────────
    if (!hasLemitAccess(profile)) {
        return (
            <div className="flex items-center justify-center py-24 animate-fade-in">
                <div className={`${card} max-w-md p-8 text-center`}>
                    <div className="mx-auto w-14 h-14 rounded-2xl bg-red-50 dark:bg-red-900/15 text-red-500 flex items-center justify-center mb-4">
                        <Ban size={26} />
                    </div>
                    <h3 className="font-poppins font-bold text-lg text-slate-800 dark:text-slate-100">Acesso restrito</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-2">
                        A Central Lemit está liberada para o <b>Comercial</b> (Closer, BDR e Hunter) e para os departamentos <b>Faturamento</b>,{' '}
                        <b>Financeiro</b>, <b>Pós Venda</b>, <b>Tecnologia</b> e <b>Marketing</b>.
                    </p>
                    <p className="text-xs text-slate-400 mt-3">Fale com um administrador para ajustar seu departamento.</p>
                </div>
            </div>
        );
    }

    const isEmpresa = result?.tipo === 'empresa';
    const pessoa = result && result.tipo === 'pessoa' ? (result.data as LemitPessoaData) : null;
    const empresa = result && result.tipo === 'empresa' ? (result.data as LemitEmpresaData) : null;
    const displayName = isEmpresa ? (empresa?.razao_social || empresa?.nome_fantasia) : pessoa?.nome;

    // Chips escalares do saldo (formato do endpoint /saldo não é fixo)
    const saldoChips: [string, any][] = saldo && typeof saldo === 'object' && !Array.isArray(saldo)
        ? Object.entries(saldo).filter(([, v]) => v == null || typeof v !== 'object').slice(0, 3)
        : [];

    // Rótulo do período ativo (mostra o intervalo real quando é filtro de datas custom).
    const rangeLabel = period === 'custom'
        ? `${customFrom ? fmtDate(customFrom) : '…'} – ${customTo ? fmtDate(customTo) : 'hoje'}`
        : PERIOD_LABEL[period];

    return (
        <div ref={topRef} className="space-y-6 pb-10">

            {/* ── Hero / command deck ─────────────────────────────────────────── */}
            <div className="relative overflow-hidden rounded-3xl bg-[#161b0c] text-white shadow-xl animate-fade-in">
                {/* atmosfera: glows + ruído + linhas de varredura */}
                <div className="absolute inset-0 pointer-events-none">
                    <div className="absolute -top-32 -left-24 w-96 h-96 rounded-full bg-[#5F6C37]/40 blur-[110px]" />
                    <div className="absolute -bottom-40 right-0 w-[28rem] h-[28rem] rounded-full bg-[#F6A937]/15 blur-[120px]" />
                    <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: NOISE }} />
                    <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, #fff 3px, #fff 4px)' }} />
                </div>

                <div className="relative px-6 sm:px-10 pt-8 pb-16">
                    <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-6">
                        <div>
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/10 backdrop-blur-sm">
                                <span className="relative flex h-2 w-2">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#F6A937] opacity-60" />
                                    <span className="relative inline-flex rounded-full h-2 w-2 bg-[#F6A937]" />
                                </span>
                                <span className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-white/80">Inteligência de dados</span>
                            </div>
                            <h1 className="font-poppins font-bold text-3xl sm:text-4xl mt-3 leading-tight">
                                Central <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#c8d39a] to-[#F6A937]">Lemit</span>
                            </h1>
                            <p className="text-sm text-white/60 mt-1.5 max-w-lg">
                                Dossiê completo de pessoas e empresas por CPF ou CNPJ — telefones, e-mails, endereços, vínculos e quadro societário.
                            </p>
                        </div>

                        {/* Saldo ao vivo */}
                        <div className="flex flex-col items-start lg:items-end gap-2">
                            <div className="flex items-center gap-2 text-[10px] font-extrabold uppercase tracking-widest text-white/50">
                                <Gauge size={12} /> Saldo da conta
                                <button onClick={loadSaldo} disabled={saldoLoading} className="p-1 rounded-md hover:bg-white/10 transition-colors disabled:opacity-40" title="Atualizar saldo">
                                    <RefreshCw size={11} className={saldoLoading ? 'animate-spin' : ''} />
                                </button>
                            </div>
                            {saldoLoading ? (
                                <div className="h-9 w-40 rounded-xl bg-white/10 animate-pulse" />
                            ) : saldoError ? (
                                <span className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-amber-400/10 border border-amber-400/20 text-amber-300 text-[11px] font-semibold max-w-xs" title={saldoError}>
                                    <AlertTriangle size={12} className="flex-shrink-0" /> Saldo indisponível
                                </span>
                            ) : saldoChips.length > 0 ? (
                                <div className="flex flex-wrap gap-2 justify-start lg:justify-end">
                                    {saldoChips.map(([k, v]) => (
                                        <div key={k} className="px-3 py-1.5 rounded-xl bg-white/10 border border-white/10 backdrop-blur-sm">
                                            <p className="text-[9px] font-extrabold uppercase tracking-wider text-white/50">{k.replace(/_/g, ' ')}</p>
                                            <p className="text-sm font-black tabular-nums">{String(v ?? '—')}</p>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <span className="text-xs text-white/40">Sem dados de saldo.</span>
                            )}
                        </div>
                    </div>

                    {/* Command bar */}
                    <div className="mt-8 max-w-3xl mx-auto">
                        <div className="relative rounded-2xl bg-white/[0.07] border border-white/15 backdrop-blur-md shadow-2xl p-2 flex flex-col sm:flex-row gap-2 focus-within:border-[#F6A937]/50 focus-within:bg-white/[0.09] transition-all">
                            <div className="flex items-center flex-1 min-w-0 gap-3 px-3">
                                <Fingerprint size={20} className="text-[#F6A937] flex-shrink-0" />
                                <input
                                    ref={inputRef}
                                    value={maskDocInput(docDigits)}
                                    onChange={(e) => setDocDigits(e.target.value.replace(/\D/g, '').slice(0, 14))}
                                    onKeyDown={(e) => { if (e.key === 'Enter' && valorValido && !loading) openConfirm(false); }}
                                    placeholder="Digite um CPF ou CNPJ para investigar..."
                                    className="flex-1 min-w-0 h-12 bg-transparent text-base font-semibold text-white placeholder:text-white/30 focus:outline-none tracking-wide font-montserrat"
                                />
                                {apiCriterio && (
                                    <span className="hidden sm:inline-flex px-2.5 py-1 rounded-lg bg-[#F6A937]/15 border border-[#F6A937]/30 text-[#F6A937] text-[10px] font-black uppercase tracking-widest animate-scale-in">
                                        {apiCriterio}
                                    </span>
                                )}
                            </div>
                            <button
                                onClick={() => openConfirm(false)}
                                disabled={loading || !valorValido}
                                className="h-12 flex items-center justify-center gap-2 px-6 rounded-xl font-bold text-sm bg-gradient-to-r from-[#5F6C37] to-[#7a8a48] hover:from-[#6d7c3f] hover:to-[#8a9b52] text-white shadow-lg shadow-black/30 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                                {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
                                Consultar
                            </button>
                        </div>

                        {/* progresso de dígitos + custo */}
                        <div className="flex items-center justify-between mt-3 px-1">
                            <div className="flex items-center gap-1">
                                {Array.from({ length: targetLen }).map((_, i) => (
                                    <span
                                        key={i}
                                        className={`h-1 rounded-full transition-all duration-300 ${i < docDigits.length
                                            ? (valorValido ? 'w-3 bg-[#F6A937]' : 'w-3 bg-white/60')
                                            : 'w-1.5 bg-white/15'}`}
                                    />
                                ))}
                                <span className={`ml-2 text-[10px] font-bold tracking-wider ${valorValido ? 'text-[#F6A937]' : 'text-white/40'}`}>
                                    {docDigits.length === 0 ? 'CPF · 11 dígitos / CNPJ · 14 dígitos'
                                        : valorValido ? `${apiCriterio === 'cpf' ? 'CPF' : 'CNPJ'} completo — Enter para consultar`
                                            : `${docDigits.length}/${targetLen} dígitos`}
                                </span>
                            </div>
                            {custoConsulta != null && (
                                <span className="hidden sm:flex items-center gap-1.5 text-[10px] font-bold text-white/50">
                                    <Wallet size={11} /> {fmtBRL(custoConsulta)}/consulta · cache 24h sem custo
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── KPIs da central de custos (sobrepondo o hero) ────────────────── */}
            <div className="-mt-14 relative z-10 px-2 sm:px-6">
                <div className="flex flex-col xl:flex-row xl:items-end gap-3">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 flex-1">
                        {[
                            {
                                icon: <ListChecks size={16} />, tint: 'text-primary bg-primary/10',
                                label: `Consultas API · ${rangeLabel}`,
                                value: stats ? String(stats.count) : '—',
                                sub: 'batidas reais na Lemit',
                            },
                            {
                                icon: <Wallet size={16} />, tint: 'text-amber-600 bg-amber-50 dark:bg-amber-900/15 dark:text-amber-400',
                                label: `Custo estimado · ${rangeLabel}`,
                                value: stats ? (stats.custoConhecido ? fmtBRL(stats.custoTotal) : '—') : '—',
                                sub: stats && !stats.custoConhecido && stats.count > 0 ? 'configure o custo em Config › APIs' : 'com base no custo configurado',
                            },
                            {
                                icon: <Database size={16} />, tint: 'text-sky-600 bg-sky-50 dark:bg-sky-900/15 dark:text-sky-400',
                                label: 'Reuso de cache',
                                value: String(cacheStats.count),
                                sub: 'consultas atendidas sem custo',
                            },
                            {
                                icon: <TrendingDown size={16} />, tint: 'text-emerald-600 bg-emerald-50 dark:bg-emerald-900/15 dark:text-emerald-400',
                                label: 'Economia por cache',
                                value: cacheStats.conhecida ? fmtBRL(cacheStats.economia) : '—',
                                sub: 'o que o cache evitou gastar',
                            },
                        ].map((kpi, i) => (
                            <div key={kpi.label} className={`${card} p-4 animate-slide-up opacity-0`} style={{ animationDelay: `${80 + i * 60}ms` }}>
                                <div className="flex items-center gap-2">
                                    <div className={`p-1.5 rounded-lg ${kpi.tint}`}>{kpi.icon}</div>
                                    <p className="text-[10px] font-extrabold uppercase tracking-wider text-slate-400 leading-tight">{kpi.label}</p>
                                </div>
                                {logLoading ? (
                                    <div className="h-8 w-20 mt-2 rounded-lg bg-slate-100 dark:bg-slate-800 animate-pulse" />
                                ) : (
                                    <p className="text-2xl font-black text-slate-800 dark:text-slate-100 tabular-nums mt-1.5">{kpi.value}</p>
                                )}
                                <p className="text-[10px] text-slate-400 mt-0.5">{kpi.sub}</p>
                            </div>
                        ))}
                    </div>

                    {/* Toggle de período + filtro de datas custom */}
                    <div className={`${card} p-2 flex flex-wrap items-center gap-1 self-start xl:self-end animate-slide-up opacity-0`} style={{ animationDelay: '320ms' }}>
                        {([['mes', 'Este mês'], ['30d', '30 dias'], ['tudo', 'Tudo']] as [Period, string][]).map(([val, label]) => (
                            <button
                                key={val}
                                onClick={() => { setPeriod(val); setCustomFrom(''); setCustomTo(''); }}
                                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all ${period === val
                                    ? 'bg-primary text-white shadow-sm'
                                    : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                            >{label}</button>
                        ))}
                        <span className="mx-1 h-5 w-px bg-slate-200 dark:bg-slate-700" />
                        <div className={`flex items-center gap-1 rounded-xl px-1.5 py-0.5 transition-colors ${period === 'custom' ? 'bg-primary/10' : ''}`}>
                            <CalendarRange size={14} className={period === 'custom' ? 'text-primary' : 'text-slate-400'} />
                            <input
                                type="date" value={customFrom} max={customTo || undefined}
                                onChange={(e) => { setCustomFrom(e.target.value); setPeriod('custom'); }}
                                title="Data inicial"
                                className="w-[120px] px-1.5 py-1 rounded-lg text-xs font-bold bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/25"
                            />
                            <span className="text-slate-400 text-xs">→</span>
                            <input
                                type="date" value={customTo} min={customFrom || undefined}
                                onChange={(e) => { setCustomTo(e.target.value); setPeriod('custom'); }}
                                title="Data final"
                                className="w-[120px] px-1.5 py-1 rounded-lg text-xs font-bold bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/25"
                            />
                            {period === 'custom' && (customFrom || customTo) && (
                                <button onClick={() => { setPeriod('mes'); setCustomFrom(''); setCustomTo(''); }} title="Limpar datas"
                                    className="p-1 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors">
                                    <X size={13} />
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Confirmação de custo ─────────────────────────────────────────── */}
            {confirming && (
                <div className={`${card} p-5 border-amber-300/60 dark:border-amber-700/40 animate-scale-in mx-2 sm:mx-6`}>
                    <div className="flex items-start gap-3">
                        <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl text-amber-600 flex-shrink-0"><Wallet size={18} /></div>
                        <div className="flex-1 min-w-0">
                            <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                                Confirmar consulta {forceMode ? 'forçada ' : ''}de <span className="font-mono">{formatDocumento(docDigits)}</span>
                            </h4>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                Esta ação consulta a base da Lemit e {custoConsulta != null ? <>tem custo de <b>{fmtBRL(custoConsulta)}</b></> : 'pode gerar custo'}.
                                {!forceMode && ' Se houver consulta recente (24h), o cache é usado sem custo.'}
                            </p>
                            {docHistory.length > 0 && (
                                <div className="mt-3 text-[11px] text-slate-500 dark:text-slate-400">
                                    <p className="font-bold uppercase tracking-wide text-slate-400 mb-1">Este documento já foi consultado</p>
                                    <ul className="space-y-1 max-h-24 overflow-y-auto">
                                        {docHistory.slice(0, 4).map((h) => (
                                            <li key={h.id} className="flex items-center gap-2">
                                                <Clock size={11} className="text-slate-300 flex-shrink-0" />
                                                {h.consultado_por_nome || 'Usuário'} · {timeAgo(h.created_at)}
                                                <span className="text-slate-300">({h.source || 'api'})</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <div className="flex justify-end gap-2 mt-4">
                                <button onClick={() => setConfirming(false)} className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                                    Cancelar
                                </button>
                                <button onClick={() => runConsulta(forceMode)} className="px-4 py-2 text-xs font-bold text-white bg-[#5F6C37] hover:bg-[#4d5a2c] rounded-xl shadow-sm transition-colors">
                                    Confirmar e consultar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Tabs ─────────────────────────────────────────────────────────── */}
            <div className="px-2 sm:px-6">
                <div className="flex items-center gap-1 border-b border-slate-200 dark:border-slate-800">
                    {([['dossie', 'Dossiê', Fingerprint], ['historico', 'Histórico de consultas', History], ['pendentes', 'Sócios pendentes', Users], ['geocode', 'Controle de Custos', Wallet]] as [typeof tab, string, any][]).map(([val, label, Icon]) => (
                        <button
                            key={val}
                            onClick={() => setTab(val)}
                            className={`relative flex items-center gap-2 px-4 py-3 text-sm font-bold transition-colors ${tab === val
                                ? 'text-primary'
                                : 'text-slate-400 hover:text-slate-600 dark:hover:text-slate-300'}`}
                        >
                            <Icon size={15} />
                            {label}
                            {val === 'historico' && log.length > 0 && (
                                <span className="px-1.5 py-0.5 text-[9px] font-black bg-slate-100 dark:bg-slate-800 text-slate-500 rounded-full">{log.length}</span>
                            )}
                            {val === 'pendentes' && pendentes.length > 0 && (
                                <span className="px-1.5 py-0.5 text-[9px] font-black bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 rounded-full">{pendentes.length}</span>
                            )}
                            {tab === val && <span className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full bg-gradient-to-r from-[#5F6C37] to-[#F6A937]" />}
                        </button>
                    ))}
                </div>
            </div>

            {/* ── Conteúdo: Dossiê ─────────────────────────────────────────────── */}
            {tab === 'dossie' && (
                <div className="px-2 sm:px-6 space-y-4">

                    {loading && (
                        <div className="flex flex-col items-center justify-center py-20 gap-4">
                            <div className="relative">
                                <div className="w-16 h-16 rounded-full border-2 border-primary/20" />
                                <div className="absolute inset-0 w-16 h-16 rounded-full border-2 border-transparent border-t-primary animate-spin" />
                                <Fingerprint size={24} className="absolute inset-0 m-auto text-primary" />
                            </div>
                            <p className="text-sm font-semibold text-slate-500">Varrendo a base da Lemit...</p>
                        </div>
                    )}

                    {error && !loading && (
                        /nao encontrado|não encontrado|\[404\]/i.test(error) ? (
                            <div className="flex items-start gap-3 px-5 py-4 rounded-2xl bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/40 animate-scale-in">
                                <div className="p-2 rounded-xl bg-amber-100 dark:bg-amber-900/30 text-amber-600 flex-shrink-0"><Fingerprint size={18} /></div>
                                <div>
                                    <p className="text-sm font-bold text-amber-800 dark:text-amber-300">Documento não encontrado na base da Lemit</p>
                                    <p className="text-xs text-amber-700/80 dark:text-amber-400/80 mt-0.5">
                                        O documento <span className="font-mono font-bold">{formatDocumento(docDigits)}</span> não retornou registro. Confira os dígitos ou tente outro documento — consultas sem resultado não geram custo.
                                    </p>
                                </div>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-red-50 dark:bg-red-900/10 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/40 text-sm font-semibold animate-scale-in">
                                <AlertTriangle size={16} /> {error}
                            </div>
                        )
                    )}

                    {/* Estado vazio com consultas recentes */}
                    {!result && !loading && !error && (
                        <div className="py-14 flex flex-col items-center text-center animate-fade-in">
                            <div className="relative w-28 h-28 mb-5">
                                <div className="absolute inset-0 rounded-full border border-slate-200 dark:border-slate-800" />
                                <div className="absolute inset-3 rounded-full border border-slate-200/80 dark:border-slate-800/80" />
                                <div className="absolute inset-6 rounded-full border border-slate-200/60 dark:border-slate-800/60" />
                                <div className="absolute inset-0 flex items-center justify-center">
                                    <Fingerprint size={30} className="text-slate-300 dark:text-slate-600" />
                                </div>
                                <div className="absolute inset-0 rounded-full animate-pulse-glow" />
                            </div>
                            <h3 className="font-poppins font-bold text-slate-700 dark:text-slate-200">Nenhum dossiê aberto</h3>
                            <p className="text-sm text-slate-400 mt-1 max-w-sm">
                                Digite um CPF ou CNPJ na barra acima — ou reabra uma consulta recente sem custo.
                            </p>
                            {log.length > 0 && (
                                <div className="flex flex-wrap justify-center gap-2 mt-6 max-w-2xl">
                                    {log.slice(0, 6).map((r) => (
                                        <button
                                            key={r.id}
                                            onClick={() => openDossieFromLog(r)}
                                            disabled={openingId != null}
                                            className="group flex items-center gap-2 px-3 py-2 rounded-xl bg-white dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 hover:border-primary/40 hover:shadow-md transition-all text-left disabled:opacity-50"
                                        >
                                            {openingId === r.id
                                                ? <Loader2 size={13} className="animate-spin text-primary" />
                                                : (r.tipo === 'empresa' ? <Building2 size={13} className="text-slate-400 group-hover:text-primary transition-colors" /> : <User size={13} className="text-slate-400 group-hover:text-primary transition-colors" />)}
                                            <div>
                                                <p className="text-xs font-bold text-slate-700 dark:text-slate-200 font-mono">{formatDocumento(r.documento)}</p>
                                                <p className="text-[10px] text-slate-400">{timeAgo(r.created_at)} · {r.consultado_por_nome || '—'}</p>
                                            </div>
                                            <ArrowUpRight size={12} className="text-slate-300 group-hover:text-primary transition-colors" />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Resultado */}
                    {result && !loading && (
                        <>
                            {/* Cartão de identidade */}
                            <div className={`${card} animate-slide-up opacity-0 relative`} style={{ animationDelay: '0ms' }}>
                                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#5F6C37] via-[#8a9b52] to-[#F6A937]" />
                                <div className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#5F6C37] to-[#212913] text-white flex items-center justify-center font-poppins font-bold text-xl shadow-lg flex-shrink-0">
                                        {isEmpresa ? <Building2 size={26} /> : initials(displayName)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h2 className="font-poppins font-bold text-xl text-slate-800 dark:text-slate-100 truncate">{displayName || '—'}</h2>
                                            {pessoa?.falecido && (
                                                <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400">CONSTA ÓBITO</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap mt-1.5 text-[11px] font-bold">
                                            <button
                                                onClick={() => copy('doc', formatDocumento(isEmpresa ? empresa?.cnpj : pessoa?.cpf))}
                                                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 font-mono hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                                title="Copiar documento"
                                            >
                                                {formatDocumento(isEmpresa ? empresa?.cnpj : pessoa?.cpf)}
                                                {copiedKey === 'doc' ? <Check size={11} className="text-emerald-500" /> : <Copy size={11} className="text-slate-400" />}
                                            </button>
                                            <span className="px-2.5 py-1 rounded-lg bg-primary/10 text-primary uppercase tracking-wide">{isEmpresa ? 'Pessoa Jurídica' : 'Pessoa Física'}</span>
                                            {(isEmpresa ? empresa?.situacao : pessoa?.situacao_cpf) && (
                                                <span className="px-2.5 py-1 rounded-lg bg-emerald-50 dark:bg-emerald-900/15 text-emerald-700 dark:text-emerald-400">
                                                    {isEmpresa ? empresa?.situacao : pessoa?.situacao_cpf}
                                                </span>
                                            )}
                                            {pessoa?.risco_credito?.score_credito && (
                                                <span className="px-2.5 py-1 rounded-lg bg-indigo-50 dark:bg-indigo-900/15 text-indigo-700 dark:text-indigo-400">
                                                    Score: {pessoa.risco_credito.score_credito}
                                                </span>
                                            )}
                                            {pessoa?.renda != null && (
                                                <span className="px-2.5 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/15 text-amber-700 dark:text-amber-400">
                                                    Renda est.: {fmtBRL(pessoa.renda)}
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-start sm:items-end gap-2 flex-shrink-0">
                                        <span className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold ${result.source === 'cache'
                                            ? 'bg-sky-50 dark:bg-sky-900/15 text-sky-700 dark:text-sky-400'
                                            : 'bg-emerald-50 dark:bg-emerald-900/15 text-emerald-700 dark:text-emerald-400'}`}>
                                            <CheckCircle2 size={12} />
                                            {result.source === 'cache' ? 'Dados do cache' : 'Consulta nova'}
                                            {result.cached_by ? ` · ${result.cached_by}` : ''} {timeAgo(result.cached_at)}
                                        </span>
                                        <div className="flex flex-wrap items-center gap-1.5 justify-start sm:justify-end">
                                            {sociosDaEmpresa.length > 0 && (
                                                <button
                                                    onClick={() => abrirConfirmSocios(true)}
                                                    disabled={sociosPrep || !!sociosOp}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-white bg-gradient-to-r from-[#5F6C37] to-[#7a8a48] hover:from-[#6d7c3f] hover:to-[#8a9b52] shadow-sm transition-all disabled:opacity-50"
                                                    title="Consulta o CPF/CNPJ de todos os sócios e baixa um PDF único com tudo"
                                                >
                                                    {sociosPrep || sociosOp ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                                    Sócios + PDF
                                                </button>
                                            )}
                                            {sociosDaEmpresa.length > 0 && (
                                                <button
                                                    onClick={() => setCadeiaSetup((v) => !v)}
                                                    disabled={sociosPrep || !!sociosOp || !!cadeiaOp}
                                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-[#5F6C37] dark:text-[#a8b87a] bg-[#5F6C37]/10 hover:bg-[#5F6C37]/20 border border-[#5F6C37]/25 transition-colors disabled:opacity-50"
                                                    title="Abre o quadro societário de cada sócio que é CNPJ, nível a nível, até chegar nos CPFs"
                                                >
                                                    {cadeiaOp ? <Loader2 size={12} className="animate-spin" /> : <Network size={12} />}
                                                    Cadeia completa
                                                </button>
                                            )}
                                            <button
                                                onClick={() => gerarPdfDossie()}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-slate-600 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                                                title="Baixar este dossiê em PDF (sem custo)"
                                            >
                                                <FileDown size={12} /> Exportar PDF
                                            </button>
                                            <button
                                                onClick={() => openConfirm(true)}
                                                disabled={loading}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-slate-500 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors disabled:opacity-50"
                                                title="Forçar nova consulta (gera custo)"
                                            >
                                                <RefreshCw size={12} /> Reconsultar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Confirmação de custo do lote de sócios */}
                            {sociosConfirm && previaSocios && (
                                <div className={`${card} p-5 border-amber-300/60 dark:border-amber-700/40 animate-scale-in`}>
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl text-amber-600 flex-shrink-0"><Users size={18} /></div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                                                Montar o dossiê de {sociosConfirm.alvos.length} sócio(s)
                                            </h4>
                                            <div className="flex flex-wrap gap-2 mt-2.5">
                                                <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-sky-50 dark:bg-sky-900/15 text-sky-700 dark:text-sky-400">
                                                    {previaSocios.cacheados} já consultado(s) · sem custo
                                                </span>
                                                <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-amber-50 dark:bg-amber-900/15 text-amber-700 dark:text-amber-400">
                                                    {previaSocios.novos} consulta(s) nova(s)
                                                </span>
                                                <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                                    Custo estimado: {previaSocios.custo != null ? fmtBRL(previaSocios.custo) : 'não configurado'}
                                                </span>
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2.5">
                                                Cada sócio novo é uma consulta paga de CPF/CNPJ na Lemit. Os que já foram
                                                consultados antes são reaproveitados do banco, sem gerar custo.
                                                {sociosConfirm.gerarPdf && ' Ao terminar, o PDF com a empresa e todos os sócios é baixado automaticamente.'}
                                            </p>
                                            <div className="flex justify-end gap-2 mt-4">
                                                <button onClick={() => setSociosConfirm(null)} className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                                                    Cancelar
                                                </button>
                                                <button onClick={rodarEnriquecimentoSocios} className="px-4 py-2 text-xs font-bold text-white bg-[#5F6C37] hover:bg-[#4d5a2c] rounded-xl shadow-sm transition-colors">
                                                    {previaSocios.novos > 0 ? `Confirmar e consultar ${previaSocios.novos}` : 'Continuar sem custo'}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Progresso do lote de sócios */}
                            {sociosOp && (
                                <div className={`${card} p-4 animate-scale-in`}>
                                    <div className="flex items-center gap-3">
                                        <Loader2 size={16} className="animate-spin text-primary flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between text-[11px] font-bold text-slate-600 dark:text-slate-300">
                                                <span>Consultando sócios — {sociosOp.done} de {sociosOp.total}</span>
                                                <span className="text-emerald-600 dark:text-emerald-400">{sociosOp.ok} com dossiê</span>
                                            </div>
                                            <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                                                <div className="h-full rounded-full bg-gradient-to-r from-[#5F6C37] to-[#F6A937] transition-all duration-300"
                                                    style={{ width: `${Math.round((sociosOp.done / Math.max(sociosOp.total, 1)) * 100)}%` }} />
                                            </div>
                                        </div>
                                        <button onClick={() => { sociosCancelRef.current = true; }}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0">
                                            <StopCircle size={13} /> Parar
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Configuração da expansão recursiva da cadeia societária */}
                            {cadeiaSetup && !cadeiaOp && (
                                <div className={`${card} p-5 border-[#5F6C37]/40 animate-scale-in`}>
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 bg-[#5F6C37]/10 rounded-xl text-[#5F6C37] dark:text-[#a8b87a] flex-shrink-0"><Network size={18} /></div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">Percorrer a cadeia societária</h4>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1.5">
                                                Cada sócio que é CNPJ tem o próprio quadro societário aberto, e assim por diante,
                                                até sobrar só pessoa física. Cada nível pede confirmação antes de gastar, e o que
                                                já foi consultado antes entra de graça. Empresa repetida não é consultada duas vezes.
                                            </p>
                                            <div className="grid sm:grid-cols-2 gap-4 mt-4">
                                                <div>
                                                    <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1.5">Profundidade máxima</p>
                                                    <div className="flex gap-1.5">
                                                        {[1, 2, 3, 5].map((n) => (
                                                            <button key={n} onClick={() => setCadeiaCfg((c) => ({ ...c, maxNivel: n }))}
                                                                className={`px-3 py-1.5 rounded-lg text-[11px] font-bold transition-colors ${cadeiaCfg.maxNivel === n
                                                                    ? 'bg-[#5F6C37] text-white shadow-sm'
                                                                    : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'}`}>
                                                                {n} {n === 1 ? 'nível' : 'níveis'}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-1.5">Teto de consultas novas</p>
                                                    <div className="flex items-center gap-2">
                                                        <input
                                                            type="number" min={1} max={300} value={cadeiaCfg.maxConsultas}
                                                            onChange={(e) => setCadeiaCfg((c) => ({ ...c, maxConsultas: Math.max(1, Math.min(300, Number(e.target.value) || 1)) }))}
                                                            className="w-24 px-3 py-1.5 rounded-lg text-xs font-bold bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/30"
                                                        />
                                                        <span className="text-[11px] text-slate-500 dark:text-slate-400">
                                                            até {custoEmpresa != null ? fmtBRL(cadeiaCfg.maxConsultas * custoEmpresa) : '—'} no pior caso
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                            <label className="flex items-center gap-2 mt-4 text-xs font-semibold text-slate-600 dark:text-slate-300 cursor-pointer">
                                                <input type="checkbox" checked={cadeiaCfg.gerarPdf}
                                                    onChange={(e) => setCadeiaCfg((c) => ({ ...c, gerarPdf: e.target.checked }))}
                                                    className="rounded border-slate-300 text-primary focus:ring-primary/30" />
                                                Baixar o PDF completo ao terminar (empresa + árvore + uma página por documento)
                                            </label>
                                            <div className="flex justify-end gap-2 mt-4">
                                                <button onClick={() => setCadeiaSetup(false)} className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                                                    Cancelar
                                                </button>
                                                <button onClick={rodarCadeia} className="px-4 py-2 text-xs font-bold text-white bg-[#5F6C37] hover:bg-[#4d5a2c] rounded-xl shadow-sm transition-colors">
                                                    Começar pelo nível 1 ({sociosDaEmpresa.length} sócio(s))
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Confirmação de custo — uma por nível da cadeia */}
                            {cadeiaConfirm && (
                                <div className={`${card} p-5 border-amber-300/60 dark:border-amber-700/40 animate-scale-in`}>
                                    <div className="flex items-start gap-3">
                                        <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-xl text-amber-600 flex-shrink-0"><CornerDownRight size={18} /></div>
                                        <div className="flex-1 min-w-0">
                                            <h4 className="text-sm font-bold text-slate-800 dark:text-slate-100">
                                                Nível {cadeiaConfirm.nivel} — {cadeiaConfirm.total} documento(s) para abrir
                                            </h4>
                                            <div className="flex flex-wrap gap-2 mt-2.5">
                                                <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-sky-50 dark:bg-sky-900/15 text-sky-700 dark:text-sky-400">
                                                    {cadeiaConfirm.cacheados} já consultado(s) · sem custo
                                                </span>
                                                <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-amber-50 dark:bg-amber-900/15 text-amber-700 dark:text-amber-400">
                                                    {cadeiaConfirm.novosEmpresa} CNPJ + {cadeiaConfirm.novosPessoa} CPF novo(s)
                                                </span>
                                                <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                                    Custo deste nível: {cadeiaConfirm.custo != null ? fmtBRL(cadeiaConfirm.custo) : 'não configurado'}
                                                </span>
                                                {cadeiaConfirm.novosAcumulados > 0 && (
                                                    <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-500">
                                                        Já gasto nesta expansão: {cadeiaConfirm.custoAcumulado != null ? fmtBRL(cadeiaConfirm.custoAcumulado) : `${cadeiaConfirm.novosAcumulados} consulta(s)`}
                                                    </span>
                                                )}
                                            </div>
                                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-2.5">
                                                Parar aqui mantém tudo que já foi levantado — o dossiê sai com a cadeia até este ponto.
                                            </p>
                                            <div className="flex justify-end gap-2 mt-4">
                                                <button onClick={() => responderNivelCadeia(false)} className="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors">
                                                    Parar aqui
                                                </button>
                                                <button onClick={() => responderNivelCadeia(true)} className="px-4 py-2 text-xs font-bold text-white bg-[#5F6C37] hover:bg-[#4d5a2c] rounded-xl shadow-sm transition-colors">
                                                    Descer o nível {cadeiaConfirm.nivel}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Progresso da cadeia */}
                            {cadeiaOp && !cadeiaConfirm && (
                                <div className={`${card} p-4 animate-scale-in`}>
                                    <div className="flex items-center gap-3">
                                        <Loader2 size={16} className="animate-spin text-primary flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between text-[11px] font-bold text-slate-600 dark:text-slate-300">
                                                <span>Cadeia · nível {cadeiaOp.nivel} — {cadeiaOp.done} de {cadeiaOp.total}</span>
                                            </div>
                                            <div className="mt-1.5 h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                                                <div className="h-full rounded-full bg-gradient-to-r from-[#5F6C37] to-[#F6A937] transition-all duration-300"
                                                    style={{ width: `${Math.round((cadeiaOp.done / Math.max(cadeiaOp.total, 1)) * 100)}%` }} />
                                            </div>
                                        </div>
                                        <button onClick={() => { cadeiaCancelRef.current = true; }}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0">
                                            <StopCircle size={13} /> Parar
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Dados cadastrais */}
                            <SectionCard icon={isEmpresa ? <Building2 size={14} /> : <User size={14} />} title="Dados cadastrais" tint="text-blue-700 bg-blue-50 dark:bg-blue-900/15 dark:text-blue-400" delay={40}>
                                {isEmpresa ? (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        <InfoCell label="Razão social" value={empresa?.razao_social} />
                                        <InfoCell label="Nome fantasia" value={empresa?.nome_fantasia} />
                                        <InfoCell label="CNPJ" value={formatDocumento(empresa?.cnpj)} />
                                        <InfoCell label="Situação" value={empresa?.situacao} />
                                        <InfoCell label="Tipo" value={empresa?.tipo} />
                                        <InfoCell label="Fundação" value={fmtDate(empresa?.data_fundacao)} />
                                        <InfoCell label="CNAE" value={empresa?.cnae ? `${empresa.cnae.numero || ''} ${empresa.cnae.descricao || ''}`.trim() : '—'} />
                                        <InfoCell label="Segmento CNAE" value={empresa?.cnae?.segmento} />
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                        <InfoCell label="Nome" value={pessoa?.nome} />
                                        <InfoCell label="CPF" value={formatDocumento(pessoa?.cpf)} />
                                        <InfoCell label="Nascimento" value={fmtDate(pessoa?.data_nascimento)} />
                                        <InfoCell label="Sexo" value={pessoa?.sexo} />
                                        <InfoCell label="Nome da mãe" value={pessoa?.nome_mae} />
                                        <InfoCell label="Situação CPF" value={pessoa?.situacao_cpf} />
                                        <InfoCell label="Renda estimada" value={pessoa?.renda != null ? fmtBRL(pessoa.renda) : '—'} />
                                        <InfoCell label="Ocupação" value={pessoa?.ocupacao} />
                                        <InfoCell label="Risco de crédito" value={pessoa?.risco_credito?.score_credito} />
                                    </div>
                                )}
                            </SectionCard>

                            {/* Telefones + E-mails lado a lado */}
                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
                                <SectionCard icon={<Phone size={14} />} title="Telefones" count={((result.data as any).celulares?.length || 0) + ((result.data as any).fixos?.length || 0)} tint="text-emerald-700 bg-emerald-50 dark:bg-emerald-900/15 dark:text-emerald-400" delay={80}>
                                    {(() => {
                                        const tels: LemitTelefone[] = [...((result.data as any).celulares || []), ...((result.data as any).fixos || [])];
                                        if (!tels.length) return <EmptyRow>Nenhum telefone retornado</EmptyRow>;
                                        return (
                                            <div className="flex flex-wrap gap-2">
                                                {tels.sort((a, b) => (a.ranking ?? 99) - (b.ranking ?? 99)).map((p, i) => {
                                                    const digits = `${p.ddd ?? ''}${(p.numero ?? '').replace(/\D/g, '')}`;
                                                    return (
                                                        <div key={i} className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/60 dark:border-slate-700 group">
                                                            <span className="font-mono font-bold text-sm text-slate-700 dark:text-slate-200">{formatPhone(p)}</span>
                                                            {p.whatsapp && (
                                                                <a href={`https://wa.me/55${digits}`} target="_blank" rel="noreferrer" className="px-1.5 py-0.5 text-[9px] font-bold bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 rounded hover:bg-emerald-200 transition-colors">
                                                                    WhatsApp
                                                                </a>
                                                            )}
                                                            {p.ranking === 1 && <span className="px-1.5 py-0.5 text-[9px] font-bold bg-primary/10 text-primary rounded">melhor</span>}
                                                            <button onClick={() => copy(`tel-${i}`, digits)} className="opacity-0 group-hover:opacity-100 transition-opacity" title="Copiar">
                                                                {copiedKey === `tel-${i}` ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} className="text-slate-400 hover:text-slate-600" />}
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        );
                                    })()}
                                </SectionCard>

                                <SectionCard icon={<Mail size={14} />} title="E-mails" count={(result.data as any).emails?.length || 0} tint="text-sky-700 bg-sky-50 dark:bg-sky-900/15 dark:text-sky-400" delay={100}>
                                    {!(result.data as any).emails?.length ? <EmptyRow>Nenhum e-mail retornado</EmptyRow> : (
                                        <div className="flex flex-wrap gap-2">
                                            {(result.data as any).emails.map((e: any, i: number) => (
                                                <div key={i} className="flex items-center gap-2 px-3 py-2 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/60 dark:border-slate-700 group">
                                                    <span className="text-sm text-slate-700 dark:text-slate-200 break-all">{e.email}</span>
                                                    {e.ranking === 1 && <span className="px-1.5 py-0.5 text-[9px] font-bold bg-primary/10 text-primary rounded flex-shrink-0">melhor</span>}
                                                    <button onClick={() => copy(`mail-${i}`, e.email)} className="opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" title="Copiar">
                                                        {copiedKey === `mail-${i}` ? <Check size={12} className="text-emerald-500" /> : <Copy size={12} className="text-slate-400 hover:text-slate-600" />}
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </SectionCard>
                            </div>

                            {/* Endereços */}
                            <SectionCard icon={<MapPin size={14} />} title="Endereços" count={isEmpresa ? (empresa?.endereco ? 1 : 0) : (pessoa?.enderecos?.length || 0)} tint="text-rose-700 bg-rose-50 dark:bg-rose-900/15 dark:text-rose-400" delay={120}>
                                {(() => {
                                    const ends = isEmpresa ? (empresa?.endereco ? [empresa.endereco] : []) : (pessoa?.enderecos || []);
                                    if (!ends.length) return <EmptyRow>Nenhum endereço retornado</EmptyRow>;
                                    return (
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                            {ends.map((e, i) => (
                                                <div key={i} className="px-3 py-2.5 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/60 dark:border-slate-700 text-sm">
                                                    <p className="font-semibold text-slate-700 dark:text-slate-200">{e?.endereco || [e?.logradouro, e?.numero].filter(Boolean).join(', ') || '—'}</p>
                                                    <p className="text-xs text-slate-500 dark:text-slate-400">
                                                        {[e?.bairro, e?.cidade && e?.uf ? `${e.cidade}/${e.uf}` : e?.cidade, e?.cep].filter(Boolean).join(' · ')}
                                                    </p>
                                                </div>
                                            ))}
                                        </div>
                                    );
                                })()}
                            </SectionCard>

                            {/* Veículos */}
                            {(result.data as any).carros?.length > 0 && (
                                <SectionCard icon={<Car size={14} />} title="Veículos" count={(result.data as any).carros.length} tint="text-indigo-700 bg-indigo-50 dark:bg-indigo-900/15 dark:text-indigo-400" delay={140}>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead><tr className="border-b border-slate-100 dark:border-slate-700/50 text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                                <th className="px-3 py-2">Placa</th><th className="px-3 py-2">Marca/Modelo</th><th className="px-3 py-2">Ano</th>
                                            </tr></thead>
                                            <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                                {(result.data as any).carros.map((c: any, i: number) => (
                                                    <tr key={i}>
                                                        <td className="px-3 py-2 font-mono font-bold text-slate-700 dark:text-slate-200">{c.placa || '—'}</td>
                                                        <td className="px-3 py-2 text-slate-600 dark:text-slate-300">{c.marca || '—'}</td>
                                                        <td className="px-3 py-2 text-slate-500">{[c.ano_fabricacao, c.ano_modelo].filter(Boolean).join('/') || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </SectionCard>
                            )}

                            {/* Vínculos familiares (pessoa) */}
                            {!isEmpresa && pessoa?.vinculos && pessoa.vinculos.length > 0 && (
                                <SectionCard icon={<Link2 size={14} />} title="Vínculos familiares" count={pessoa.vinculos.length} delay={160}>
                                    <div className="flex flex-wrap gap-2">
                                        {pessoa.vinculos.map((v, i) => (
                                            <div key={i} className="px-3 py-2 bg-slate-50 dark:bg-slate-800/60 rounded-xl border border-slate-200/60 dark:border-slate-700 text-xs">
                                                <span className="font-semibold text-slate-700 dark:text-slate-200">{v.nome_vinculo}</span>
                                                <span className="ml-2 px-1.5 py-0.5 bg-primary/10 text-primary rounded text-[9px] font-bold uppercase">{v.tipo_vinculo}</span>
                                            </div>
                                        ))}
                                    </div>
                                </SectionCard>
                            )}

                            {/* Participações societárias (pessoa) */}
                            {!isEmpresa && pessoa?.participacao_societaria && pessoa.participacao_societaria.length > 0 && (
                                <SectionCard icon={<Building2 size={14} />} title="Participações societárias" count={pessoa.participacao_societaria.length} delay={180}>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead><tr className="border-b border-slate-100 dark:border-slate-700/50 text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                                <th className="px-3 py-2">CNPJ</th><th className="px-3 py-2">Empresa</th><th className="px-3 py-2 text-center">Participação</th><th className="px-3 py-2 text-center">Situação</th>
                                            </tr></thead>
                                            <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                                {pessoa.participacao_societaria.map((p, i) => (
                                                    <tr key={i}>
                                                        <td className="px-3 py-2 font-mono text-slate-600 dark:text-slate-300">{formatDocumento(p.cnpj)}</td>
                                                        <td className="px-3 py-2 font-bold text-slate-700 dark:text-slate-200">{p.nome}</td>
                                                        <td className="px-3 py-2 text-center font-bold text-primary">{p.participacao_socio != null ? `${p.participacao_socio}%` : '—'}</td>
                                                        <td className="px-3 py-2 text-center text-slate-500">{p.situacao_cadastral || '—'}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </SectionCard>
                            )}

                            {/* Quadro societário (empresa) */}
                            {isEmpresa && empresa?.socios && empresa.socios.length > 0 && (
                                <SectionCard
                                    icon={<Users size={14} />} title="Quadro societário" count={empresa.socios.length} delay={180}
                                    action={
                                        <button
                                            onClick={() => abrirConfirmSocios(false)}
                                            disabled={sociosPrep || !!sociosOp || sociosFaltando.length === 0}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-primary bg-primary/10 hover:bg-primary/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            title={sociosFaltando.length === 0 ? 'Todos os sócios já têm dados salvos' : 'Consultar o documento de cada sócio na Lemit'}
                                        >
                                            {sociosPrep || sociosOp ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                            {sociosOp ? `${sociosOp.done}/${sociosOp.total}`
                                                : sociosFaltando.length === 0 ? 'Todos enriquecidos'
                                                    : `Enriquecer ${sociosFaltando.length} sócio(s)`}
                                        </button>
                                    }
                                >
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead><tr className="border-b border-slate-100 dark:border-slate-700/50 text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                                <th className="px-3 py-2">Nome</th><th className="px-3 py-2">CPF/CNPJ</th><th className="px-3 py-2 text-center">Part.</th><th className="px-3 py-2">Melhor contato</th><th className="px-3 py-2"></th>
                                            </tr></thead>
                                            <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                                {empresa.socios.map((s, i) => {
                                                    const socioDigits = (s.cpf || '').replace(/\D/g, '');
                                                    const enr = sociosEnr[socioDigits];
                                                    return (
                                                        <tr key={i} className="align-top">
                                                            <td className="px-3 py-2 font-bold text-slate-700 dark:text-slate-200">{s.nome}</td>
                                                            <td className="px-3 py-2 font-mono text-slate-500 whitespace-nowrap">{formatDocumento(s.cpf)}</td>
                                                            <td className="px-3 py-2 text-center font-bold text-primary">{s.participacao != null ? `${s.participacao}%` : '—'}</td>
                                                            <td className="px-3 py-2">
                                                                {!enr ? (
                                                                    <span className="text-slate-300 dark:text-slate-600">—</span>
                                                                ) : enr.status !== 'ok' ? (
                                                                    <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 dark:text-amber-400" title={enr.erro}>
                                                                        <AlertTriangle size={10} />
                                                                        {enr.status === 'nao_encontrado' ? 'não encontrado na Lemit'
                                                                            : enr.status === 'sem_documento' ? 'sem documento' : 'falhou'}
                                                                    </span>
                                                                ) : (
                                                                    <div className="space-y-0.5">
                                                                        <p className="font-mono font-bold text-slate-700 dark:text-slate-200">{enr.melhorTelefone || '—'}</p>
                                                                        <p className="text-[10px] text-slate-500 break-all">{enr.melhorEmail || 'sem e-mail'}</p>
                                                                    </div>
                                                                )}
                                                            </td>
                                                            <td className="px-3 py-2 text-right whitespace-nowrap">
                                                                {(socioDigits.length === 11 || socioDigits.length === 14) && (
                                                                    <button
                                                                        onClick={() => { setDocDigits(socioDigits); topRef.current?.scrollIntoView({ behavior: 'smooth' }); inputRef.current?.focus(); }}
                                                                        className="text-[10px] font-bold text-primary hover:underline"
                                                                        title="Preencher busca com este documento"
                                                                    >
                                                                        investigar →
                                                                    </button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    <p className="text-[10px] text-slate-400 mt-3 flex items-center gap-1.5">
                                        <Database size={11} />
                                        Sócios já consultados antes aparecem preenchidos sem custo. O PDF sai com uma página completa por sócio enriquecido.
                                    </p>
                                </SectionCard>
                            )}

                            {/* Cadeia societária expandida */}
                            {cadeia && cadeia.length > 0 && (
                                <SectionCard
                                    icon={<Network size={14} />} title="Cadeia societária" count={cadeia.length} delay={200}
                                    tint="text-[#5F6C37] bg-[#5F6C37]/10 dark:text-[#a8b87a]"
                                    action={
                                        <button
                                            onClick={() => gerarPdfDossie()}
                                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold text-primary bg-primary/10 hover:bg-primary/20 transition-colors"
                                            title="PDF com a empresa, a árvore inteira e uma página por documento"
                                        >
                                            <FileDown size={12} /> PDF da cadeia
                                        </button>
                                    }
                                >
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        {(() => {
                                            const r = resumoCadeia(cadeia);
                                            return (
                                                <>
                                                    <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                                        {r.empresas} empresa(s)
                                                    </span>
                                                    <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-emerald-50 dark:bg-emerald-900/15 text-emerald-700 dark:text-emerald-400">
                                                        {pessoasCadeia.length} CPF(s) encontrado(s)
                                                    </span>
                                                    <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                                        {r.nivelMax} nível(is)
                                                    </span>
                                                    {cadeiaRes && cadeiaRes.consultasNovas > 0 && (
                                                        <span className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-amber-50 dark:bg-amber-900/15 text-amber-700 dark:text-amber-400">
                                                            {cadeiaRes.consultasNovas} consulta(s) nova(s)
                                                            {cadeiaRes.custoGasto != null ? ` · ${fmtBRL(cadeiaRes.custoGasto)}` : ''}
                                                        </span>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>

                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead><tr className="border-b border-slate-100 dark:border-slate-700/50 text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                                <th className="px-3 py-2">Estrutura</th><th className="px-3 py-2">CPF/CNPJ</th><th className="px-3 py-2 text-center">Part.</th><th className="px-3 py-2">Dossiê</th><th className="px-3 py-2"></th>
                                            </tr></thead>
                                            <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                                {cadeia.map((n, i) => (
                                                    <tr key={`${n.cpf}-${n.paiDoc}-${i}`} className="align-top">
                                                        <td className="px-3 py-2">
                                                            <div className="flex items-center gap-1.5" style={{ paddingLeft: `${(n.nivel - 1) * 16}px` }}>
                                                                {n.nivel > 1 && <CornerDownRight size={11} className="text-slate-300 dark:text-slate-600 flex-shrink-0" />}
                                                                {n.tipo === 'empresa'
                                                                    ? <Building2 size={11} className="text-[#5F6C37] flex-shrink-0" />
                                                                    : <User size={11} className="text-sky-600 flex-shrink-0" />}
                                                                <span className={`font-bold ${n.repetido ? 'text-slate-400 dark:text-slate-500' : 'text-slate-700 dark:text-slate-200'}`}>
                                                                    {n.nome || formatDocumento(n.cpf)}
                                                                </span>
                                                            </div>
                                                        </td>
                                                        <td className="px-3 py-2 font-mono text-slate-500 whitespace-nowrap">{formatDocumento(n.cpf)}</td>
                                                        <td className="px-3 py-2 text-center font-bold text-primary">{n.participacao != null ? `${n.participacao}%` : '—'}</td>
                                                        <td className="px-3 py-2">
                                                            {n.repetido ? (
                                                                <span className="text-[10px] font-bold text-slate-400">já detalhado acima</span>
                                                            ) : n.status !== 'ok' || !n.data ? (
                                                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-600 dark:text-amber-400" title={n.erro}>
                                                                    <AlertTriangle size={10} />
                                                                    {n.status === 'nao_encontrado' ? 'não encontrado' : n.status === 'sem_documento' ? 'sem documento' : n.data ? 'sem contato' : 'falhou'}
                                                                </span>
                                                            ) : (
                                                                <div className="space-y-0.5">
                                                                    <p className="font-mono font-bold text-slate-700 dark:text-slate-200">{n.melhorTelefone || '—'}</p>
                                                                    <p className="text-[10px] text-slate-500 break-all">{n.melhorEmail || 'sem e-mail'}</p>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-3 py-2 text-right whitespace-nowrap">
                                                            <button
                                                                onClick={() => { setDocDigits(n.cpf); topRef.current?.scrollIntoView({ behavior: 'smooth' }); inputRef.current?.focus(); }}
                                                                className="text-[10px] font-bold text-primary hover:underline"
                                                                title="Preencher busca com este documento"
                                                            >
                                                                investigar →
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>

                                    {pessoasCadeia.length > 0 && (
                                        <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800">
                                            <p className="text-[10px] font-extrabold text-slate-400 uppercase tracking-wider mb-2">
                                                Pessoas físicas no fim da cadeia ({pessoasCadeia.length})
                                            </p>
                                            <div className="overflow-x-auto">
                                                <table className="w-full text-xs">
                                                    <thead><tr className="border-b border-slate-100 dark:border-slate-700/50 text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                                        <th className="px-3 py-2">Nome</th><th className="px-3 py-2">CPF</th><th className="px-3 py-2">Melhor contato</th><th className="px-3 py-2">Entra por</th>
                                                    </tr></thead>
                                                    <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                                        {pessoasCadeia.map((p) => (
                                                            <tr key={p.cpf} className="align-top">
                                                                <td className="px-3 py-2 font-bold text-slate-700 dark:text-slate-200">{p.nome || '—'}</td>
                                                                <td className="px-3 py-2 font-mono text-slate-500 whitespace-nowrap">{formatDocumento(p.cpf)}</td>
                                                                <td className="px-3 py-2">
                                                                    <p className="font-mono font-bold text-slate-700 dark:text-slate-200">{p.melhorTelefone || '—'}</p>
                                                                    <p className="text-[10px] text-slate-500 break-all">{p.melhorEmail || 'sem e-mail'}</p>
                                                                </td>
                                                                <td className="px-3 py-2 text-[10px] text-slate-500">{p.vias.join(' · ') || '—'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}

                                    {cadeiaRes && (
                                        <p className="text-[10px] text-slate-400 mt-3 flex items-center gap-1.5">
                                            <Database size={11} />
                                            {textoMotivoParada(cadeiaRes)}
                                            {cadeiaRes.motivoParada === 'teto' || cadeiaRes.motivoParada === 'profundidade'
                                                ? ' Aumente a profundidade ou o teto e rode de novo — o que já foi consultado não é cobrado outra vez.'
                                                : ''}
                                        </p>
                                    )}
                                </SectionCard>
                            )}

                            {/* JSON bruto */}
                            <details className={`${card} animate-slide-up opacity-0`} style={{ animationDelay: '200ms' }}>
                                <summary className="flex items-center gap-2.5 px-5 py-3 cursor-pointer select-none list-none">
                                    <div className="p-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-500"><Code2 size={14} /></div>
                                    <span className="text-sm font-bold text-slate-700 dark:text-slate-200">Retorno completo (JSON)</span>
                                    <ChevronRight size={16} className="ml-auto text-slate-400" />
                                </summary>
                                <pre className="px-5 pb-4 text-[11px] leading-relaxed text-slate-600 dark:text-slate-300 overflow-x-auto max-h-72 overflow-y-auto">
                                    {JSON.stringify(result.raw ?? result.data, null, 2)}
                                </pre>
                            </details>
                        </>
                    )}
                </div>
            )}

            {/* ── Conteúdo: Histórico ──────────────────────────────────────────── */}
            {tab === 'historico' && (
                <div className="px-2 sm:px-6">
                    <div className={`${card} animate-fade-in`}>
                        <div className={sectionHead}>
                            <div className="p-1.5 rounded-lg bg-primary/10 text-primary"><History size={14} /></div>
                            <h4 className="text-sm font-bold text-slate-700 dark:text-slate-200">Registro de consultas · {rangeLabel}</h4>
                            <div className="ml-auto flex items-center gap-2">
                                <div className="relative">
                                    <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                    <input
                                        value={histSearch}
                                        onChange={(e) => setHistSearch(e.target.value)}
                                        placeholder="Filtrar por documento, usuário ou lead..."
                                        className="h-8 w-56 sm:w-72 pl-8 pr-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 text-xs font-medium text-slate-700 dark:text-slate-200 focus:outline-none focus:ring-2 focus:ring-primary/25 transition-all"
                                    />
                                </div>
                                <span className="text-[11px] font-semibold text-slate-400 whitespace-nowrap">{filteredLog.length} registro(s)</span>
                            </div>
                        </div>

                        {logLoading ? (
                            <div className="flex items-center justify-center py-14 gap-2 text-slate-500"><Loader2 size={18} className="animate-spin" /> Carregando...</div>
                        ) : filteredLog.length === 0 ? (
                            <div className="py-14 text-center">
                                <ShieldCheck size={28} className="mx-auto text-slate-300 dark:text-slate-600 mb-2" />
                                <p className="text-sm text-slate-400">{histSearch ? 'Nenhum registro corresponde ao filtro.' : 'Nenhuma consulta no período.'}</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                    <thead>
                                        <tr className="border-b border-slate-100 dark:border-slate-700/50 text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                            <th className="px-4 py-2.5">Quando</th>
                                            <th className="px-4 py-2.5">Quem</th>
                                            <th className="px-4 py-2.5">Documento</th>
                                            <th className="px-4 py-2.5">Tipo</th>
                                            <th className="px-4 py-2.5">Lead vinculado</th>
                                            <th className="px-4 py-2.5">Origem</th>
                                            <th className="px-4 py-2.5 text-right">Custo</th>
                                            <th className="px-4 py-2.5 text-right">Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-50 dark:divide-slate-800">
                                        {logPagina.map((row) => (
                                            <React.Fragment key={row.id}>
                                                <tr className="hover:bg-slate-50 dark:hover:bg-slate-800/40 transition-colors">
                                                    <td className="px-4 py-2.5 text-slate-500 whitespace-nowrap">{fmtDateTime(row.created_at)}</td>
                                                    <td className="px-4 py-2.5 font-semibold text-slate-700 dark:text-slate-200 whitespace-nowrap">{row.consultado_por_nome || '—'}</td>
                                                    <td className="px-4 py-2.5 font-mono text-slate-600 dark:text-slate-300 whitespace-nowrap">{formatDocumento(row.documento)}</td>
                                                    <td className="px-4 py-2.5">
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                                                            {row.tipo === 'empresa' ? <Building2 size={10} /> : <User size={10} />}{row.tipo}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2.5 text-slate-600 dark:text-slate-300 max-w-[180px] truncate" title={row.negociacoes?.titulo || row.negociacoes?.cliente_nome || ''}>
                                                        {row.negociacoes?.titulo || row.negociacoes?.cliente_nome || (row.negociacao_id ? `#${row.negociacao_id}` : '—')}
                                                    </td>
                                                    <td className="px-4 py-2.5">
                                                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${row.source === 'cache' ? 'bg-sky-100 text-sky-700 dark:bg-sky-900/20 dark:text-sky-400' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400'}`}>
                                                            {row.source === 'cache' ? 'cache' : 'API'}
                                                        </span>
                                                    </td>
                                                    <td className="px-4 py-2.5 text-right tabular-nums text-slate-600 dark:text-slate-300">{row.custo_estimado != null ? fmtBRL(row.custo_estimado) : '—'}</td>
                                                    <td className="px-4 py-2.5">
                                                        <div className="flex items-center justify-end gap-1">
                                                            <button
                                                                onClick={() => openDossieFromLog(row)}
                                                                disabled={openingId != null}
                                                                className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold text-primary hover:bg-primary/10 transition-colors disabled:opacity-50"
                                                                title="Abrir dossiê desta consulta (sem custo)"
                                                            >
                                                                {openingId === row.id ? <Loader2 size={12} className="animate-spin" /> : <Fingerprint size={12} />}
                                                                dossiê
                                                            </button>
                                                            <button onClick={() => toggleRaw(row.id)} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors" title="Ver JSON retornado">
                                                                <Code2 size={13} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                {expanded === row.id && (
                                                    <tr>
                                                        <td colSpan={8} className="px-4 pb-3 bg-slate-50/50 dark:bg-slate-800/20">
                                                            <pre className="p-3 bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-700 overflow-x-auto max-h-72 overflow-y-auto text-[11px] text-slate-600 dark:text-slate-300">
                                                                {rawCache[row.id] ? JSON.stringify(rawCache[row.id], null, 2) : 'Carregando...'}
                                                            </pre>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {filteredLog.length > 0 && (
                            <TablePagination total={filteredLog.length} pagina={logPage} pageSize={logPageSize}
                                onPagina={setLogPage} onPageSize={setLogPageSize} label="consulta(s)" />
                        )}
                    </div>

                    {stats && !stats.custoConhecido && stats.count > 0 && (
                        <p className="text-[11px] text-slate-400 mt-3 flex items-center gap-1.5">
                            <Sparkles size={12} />
                            Custo/consulta não configurado — defina <b>lemit_custo_pessoa</b> / <b>lemit_custo_empresa</b> em Config › APIs para estimar os gastos.
                        </p>
                    )}
                </div>
            )}

            {/* ── Conteúdo: Controle de Custos (Lemit + Geocode, por usuário) ──── */}
            {tab === 'geocode' && (
                <div className="px-2 sm:px-6 space-y-4">
                    {/* Consolidado por usuário — quem gastou quanto no mês */}
                    <div className={card}>
                        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                                <Wallet size={15} className="text-[#5F6C37]" /> Gasto por usuário — este mês
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                Lemit é cobrada em <b>R$</b>; o geocode do Google em <b>US$</b> — mantemos as moedas
                                separadas para o número seguir auditável. Só consultas que bateram na API (cache não conta).
                            </p>
                        </div>
                        <div className="p-4">
                            {geoLoading ? (
                                <div className="flex items-center justify-center py-10 gap-2 text-slate-500"><Loader2 size={18} className="animate-spin" /> Carregando...</div>
                            ) : gastos.length === 0 ? (
                                <p className="text-center text-sm text-slate-400 py-8">Nenhum gasto registrado neste mês.</p>
                            ) : (
                                <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                                    <table className="w-full text-xs">
                                        <thead className="bg-slate-50 dark:bg-slate-800">
                                            <tr className="text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                                <th className="px-3 py-2.5">Usuário</th>
                                                <th className="px-3 py-2.5 text-right">Consultas Lemit</th>
                                                <th className="px-3 py-2.5 text-right">Custo Lemit</th>
                                                <th className="px-3 py-2.5 text-right">Endereços geocode</th>
                                                <th className="px-3 py-2.5 text-right">Custo geocode</th>
                                                <th className="px-3 py-2.5 text-right">Último uso</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {gastos.map((g) => (
                                                <tr key={g.userId || g.nome}>
                                                    <td className="px-3 py-2 font-semibold text-slate-800 dark:text-slate-100">{g.nome}</td>
                                                    <td className="px-3 py-2 text-right text-slate-600 dark:text-slate-300">{g.lemitConsultas.toLocaleString('pt-BR')}</td>
                                                    <td className="px-3 py-2 text-right font-bold text-[#5F6C37] dark:text-[#a3b565]">{fmtBRL(g.lemitCustoBrl)}</td>
                                                    <td className="px-3 py-2 text-right text-slate-600 dark:text-slate-300">{g.geocodeEnderecos.toLocaleString('pt-BR')}</td>
                                                    <td className="px-3 py-2 text-right font-bold text-blue-600 dark:text-blue-400">{fmtUsd(g.geocodeCustoUsd)}</td>
                                                    <td className="px-3 py-2 text-right text-slate-500 whitespace-nowrap">{g.ultimoUso ? fmtDate(g.ultimoUso) : '—'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                        <tfoot className="bg-slate-50 dark:bg-slate-800/60 font-bold">
                                            <tr>
                                                <td className="px-3 py-2 text-slate-700 dark:text-slate-200">Total</td>
                                                <td className="px-3 py-2 text-right text-slate-700 dark:text-slate-200">{gastos.reduce((s, g) => s + g.lemitConsultas, 0).toLocaleString('pt-BR')}</td>
                                                <td className="px-3 py-2 text-right text-[#5F6C37] dark:text-[#a3b565]">{fmtBRL(gastos.reduce((s, g) => s + g.lemitCustoBrl, 0))}</td>
                                                <td className="px-3 py-2 text-right text-slate-700 dark:text-slate-200">{gastos.reduce((s, g) => s + g.geocodeEnderecos, 0).toLocaleString('pt-BR')}</td>
                                                <td className="px-3 py-2 text-right text-blue-600 dark:text-blue-400">{fmtUsd(gastos.reduce((s, g) => s + g.geocodeCustoUsd, 0))}</td>
                                                <td className="px-3 py-2" />
                                            </tr>
                                        </tfoot>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className={card}>
                        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between gap-3">
                            <div>
                                <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                                    <MapPin size={15} className="text-blue-500" /> Geocodificação (Google) — "Localizar no mapa"
                                </h3>
                                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                    API <b>paga</b>, ~US$ 0,005 por endereço. Usada na Lista Fria. As Rotas usam
                                    OpenStreetMap, que é gratuito e não entra aqui.
                                </p>
                            </div>
                            <button onClick={carregarGeocode} disabled={geoLoading}
                                className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 disabled:opacity-40 shrink-0">
                                {geoLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Atualizar
                            </button>
                        </div>

                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-4 border-b border-slate-100 dark:border-slate-800">
                            <div className="rounded-xl bg-slate-50 dark:bg-slate-800/40 p-3">
                                <p className="text-[10px] font-semibold text-slate-400 uppercase">Lotes no mês</p>
                                <p className="text-lg font-bold text-slate-900 dark:text-white">{geoStats?.chamadas ?? '—'}</p>
                            </div>
                            <div className="rounded-xl bg-slate-50 dark:bg-slate-800/40 p-3">
                                <p className="text-[10px] font-semibold text-slate-400 uppercase">Endereços no mês</p>
                                <p className="text-lg font-bold text-slate-900 dark:text-white">{geoStats?.enderecos?.toLocaleString('pt-BR') ?? '—'}</p>
                            </div>
                            <div className="rounded-xl bg-blue-50 dark:bg-blue-900/20 p-3">
                                <p className="text-[10px] font-semibold text-blue-600/70 dark:text-blue-400/70 uppercase">Custo no mês</p>
                                <p className="text-lg font-bold text-blue-700 dark:text-blue-300">{geoStats ? fmtUsd(geoStats.custoUsd) : '—'}</p>
                            </div>
                            <div className="rounded-xl bg-slate-50 dark:bg-slate-800/40 p-3">
                                <p className="text-[10px] font-semibold text-slate-400 uppercase">Custo total (200 últ.)</p>
                                <p className="text-lg font-bold text-slate-900 dark:text-white">
                                    {fmtUsd(geoLog.reduce((s, r) => s + Number(r.custo_estimado_usd || 0), 0))}
                                </p>
                            </div>
                        </div>

                        <div className="p-4">
                            {geoLoading ? (
                                <div className="flex items-center justify-center py-14 gap-2 text-slate-500"><Loader2 size={18} className="animate-spin" /> Carregando...</div>
                            ) : geoLog.length === 0 ? (
                                <p className="text-center text-sm text-slate-400 py-10">
                                    Nenhuma geocodificação registrada ainda. O registro começa a partir de agora —
                                    os lotes rodados antes desta versão não foram contabilizados.
                                </p>
                            ) : (
                                <div className="max-h-[440px] overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700">
                                    <table className="w-full text-xs">
                                        <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800">
                                            <tr className="text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                                <th className="px-3 py-2.5">Quando</th>
                                                <th className="px-3 py-2.5">Quem</th>
                                                <th className="px-3 py-2.5">Origem</th>
                                                <th className="px-3 py-2.5 text-right">Endereços</th>
                                                <th className="px-3 py-2.5 text-right">Sucesso</th>
                                                <th className="px-3 py-2.5 text-right">Custo</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {geoLog.map((r) => (
                                                <tr key={r.id}>
                                                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300 whitespace-nowrap">
                                                        {new Date(r.created_at).toLocaleDateString('pt-BR')} {new Date(r.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                                                    </td>
                                                    <td className="px-3 py-2 font-semibold text-slate-800 dark:text-slate-100">{r.user_nome || '—'}</td>
                                                    <td className="px-3 py-2 text-slate-500">{r.origem}</td>
                                                    <td className="px-3 py-2 text-right font-semibold text-slate-700 dark:text-slate-200">{r.enderecos.toLocaleString('pt-BR')}</td>
                                                    <td className="px-3 py-2 text-right text-slate-500">{r.sucesso.toLocaleString('pt-BR')}</td>
                                                    <td className="px-3 py-2 text-right font-bold text-blue-600 dark:text-blue-400 whitespace-nowrap">{fmtUsd(Number(r.custo_estimado_usd || 0))}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── Conteúdo: Sócios pendentes ───────────────────────────────────── */}
            {tab === 'pendentes' && (
                <div className="px-2 sm:px-6 space-y-4">
                    <div className={card}>
                        <div className="px-5 py-4 border-b border-slate-100 dark:border-slate-800">
                            <h3 className="text-sm font-bold text-slate-700 dark:text-slate-200 flex items-center gap-2">
                                <Users size={15} className="text-amber-500" /> Representantes sem dados pessoais
                            </h3>
                            <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                Estes leads já têm <b>quem</b> é o dono (veio de graça no quadro societário da empresa), mas o
                                <b> CPF dele nunca foi consultado</b> — por isso não há telefone, e-mail nem endereço pessoal.
                                A consulta de CPF é separada e paga.
                            </p>
                        </div>

                        <div className="p-4 space-y-3">
                            {/* Resumo + ações */}
                            <div className="flex flex-wrap items-center gap-2">
                                <span className="px-2.5 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 text-xs font-bold">
                                    {pendentes.length} pendente(s)
                                </span>
                                {custoPessoa != null && pendentes.length > 0 && (
                                    <span className="px-2.5 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-xs font-bold">
                                        Custo total estimado: {fmtBRL(custoPessoa * pendentes.length)}
                                    </span>
                                )}
                                <div className="flex items-center gap-2 ml-auto">
                                    <button
                                        onClick={carregarPendentes}
                                        disabled={pendLoading || !!pendOp}
                                        className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 disabled:opacity-40"
                                    >
                                        {pendLoading ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Atualizar
                                    </button>
                                    {pendentes.length > 0 && !pendOp && (
                                        <>
                                            <button
                                                onClick={() => enriquecerPendentes(pendentes.slice(0, 25))}
                                                className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-xl bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-800 hover:bg-amber-100"
                                                title="Testa com um lote pequeno antes de rodar tudo"
                                            >
                                                <Sparkles size={13} /> Enriquecer 25
                                            </button>
                                            <button
                                                onClick={() => enriquecerPendentes(pendentes)}
                                                className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold rounded-xl bg-[#5F6C37] text-white hover:bg-[#4d5a2c]"
                                            >
                                                <Sparkles size={13} /> Enriquecer todos ({pendentes.length})
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Progresso */}
                            {pendOp && (
                                <div className="flex items-center gap-3 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                                    <Loader2 size={16} className="animate-spin text-amber-600" />
                                    <div className="flex-1">
                                        <div className="flex items-center justify-between text-xs font-semibold text-amber-700 dark:text-amber-300">
                                            <span>Consultando CPFs… {pendOp.done}/{pendOp.total} · {pendOp.ok} ok</span>
                                            <button onClick={() => { pendCancelRef.current = true; }} className="hover:underline">Parar</button>
                                        </div>
                                        <div className="mt-1 h-1.5 rounded-full bg-amber-100 dark:bg-amber-900/40 overflow-hidden">
                                            <div className="h-full bg-amber-500 transition-all" style={{ width: `${pendOp.total ? (pendOp.done / pendOp.total) * 100 : 0}%` }} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Lista */}
                            {pendLoading ? (
                                <div className="flex items-center justify-center py-14 gap-2 text-slate-500"><Loader2 size={18} className="animate-spin" /> Procurando pendências...</div>
                            ) : pendentes.length === 0 ? (
                                <div className="text-center py-10 text-slate-400">
                                    <CheckCircle2 size={36} className="mx-auto mb-3 text-emerald-400 opacity-60" />
                                    <p className="text-sm">Nenhuma pendência — todos os representantes já têm os dados pessoais consultados. 🎉</p>
                                </div>
                            ) : (
                                <div className="max-h-[440px] overflow-y-auto rounded-xl border border-slate-200 dark:border-slate-700">
                                    <table className="w-full text-xs">
                                        <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800">
                                            <tr className="text-left text-[10px] uppercase text-slate-400 font-extrabold">
                                                <th className="px-3 py-2.5">Lead</th>
                                                <th className="px-3 py-2.5">Representante</th>
                                                <th className="px-3 py-2.5">CPF</th>
                                                <th className="px-3 py-2.5 text-right">Ação</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {pendPagina.map((p) => (
                                                <tr key={`${p.negociacao_id}-${p.cpf_representante}`}>
                                                    <td className="px-3 py-2 font-semibold text-slate-700 dark:text-slate-200 max-w-[220px] truncate">{p.cliente_nome}</td>
                                                    <td className="px-3 py-2 text-slate-600 dark:text-slate-300 max-w-[200px] truncate">{p.nome_representante_legal || '—'}</td>
                                                    <td className="px-3 py-2 font-mono text-slate-500">{formatDocumento(p.cpf_representante)}</td>
                                                    <td className="px-3 py-2 text-right">
                                                        <button
                                                            onClick={() => enriquecerPendentes([p])}
                                                            disabled={!!pendOp}
                                                            className="px-2.5 py-1 rounded-lg text-[11px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 disabled:opacity-40"
                                                        >
                                                            Consultar
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            {pendentes.length > 0 && (
                                <TablePagination total={pendentes.length} pagina={pendPage} pageSize={pendPageSize}
                                    onPagina={setPendPage} onPageSize={setPendPageSize} label="representante(s)" />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LemitView;
